/**
 * Surface wetness (Graphics G4) — a shared TSL uniform + a material modifier.
 *
 * `applyWetness` makes a (textureless) MeshStandardMaterial respond to rain: darker
 * albedo + lower roughness (glossier reflections) as wetness rises. WeatherModule lerps
 * `setWetness` from the active WeatherProfile. For a TEXTURED surface use
 * `applyWetnessTextured` — the plain colorNode override would drop the albedo map.
 */
import * as THREE from 'three/webgpu';
import { stochasticTexture } from 'enginex/render/materials/StochasticTiling.js';
import { applyRainRipples } from 'enginex/render/materials/RainRipples.js';
import { shelterExposure } from './rain_shelter.js';
import { applySpecularAA } from 'enginex/render/materials/SpecularAA.js';
import { specularAAHandles, stage5Stats } from './stage5_fx.js';
import { rainHandles, stage4Stats } from './stage4_fx.js';
import { uniform, mix, float, color, texture, positionWorld, mx_noise_float, vec2, vec3, attribute, clamp, normalMap, normalWorld, smoothstep, normalize, length, cameraPosition, uv, time } from 'three/tsl';
import { tslHeightBlend } from 'enginex/terrain/TerrainHeightBlend.js';
import { tslTriplanarWeights, tslWhiteoutNormalBlend } from 'enginex/terrain/TerrainSampling.js';
import { tslMicroDetailNormal, tslMicroStrength } from 'enginex/terrain/TerrainMacroMicro.js';
import { Biome, BIOMES } from '@shared/terrain.js';
// Ground material v2 (2026-09-25) — see the GROUND MATERIAL V2 section below.
import {
  Fn, If, property, int, dFdx, dFdy, max, min, abs, pow, select, sqrt, dot, fract, sin, floor,
  normalWorldGeometry, cameraViewMatrix, vec4,
  normalViewGeometry, cos, // surfaces (2026-09-27): micro-shadowing (the lighting model) + hex-tiling rotations
} from 'three/tsl';
import {
  GROUND_LAYERS, GROUND_LAYER_COUNT, GROUND_LAYER_KEYS, POTATO_PHYSICAL, POTATO_FOLD, GROUND_TILE_PERIOD_M,
  GROUND_BAND_FADE, GROUND_LAYER_MEAN, GROUND_LAYER_ROUGH, ROUTE_NONE, LAYER_INDEX, type GroundLayerKey,
  GROUND_LAYER_SAT, GROUND_LAYER_LOOK_MEAN,
  GROUND_LAYER_POROSITY, // surfaces (2026-09-27): §2.5 wetness by porosity
} from './ground_layers.js';
import { renderAnchor, onRenderAnchor } from './local_origin.js';
import { underwaterBedShading } from './water_bed.js'; // water agent (R7, 2026-09-25): the seabed's caustics + per-channel attenuation
import { applyDamageLayer } from './damage_layer.js'; // shader gaps R2 (damage): gouges / chips / char in the ground
import { damageMode } from './damage_mode.js';
import { deformImprint } from './deform_layer.js'; // weather surfaces R3: snow and mud that remember (no sampler — the shelter atlas)
import { puddleGroundTerm, ensurePuddleChannel } from './puddle_layer.js'; // shader gaps R6: the shallow-water puddle patch (a storage buffer — no sampler)
import { puddlePatchNow, RELIEF_M, RUT_M, LOOK_TEX_RELIEF } from './puddle_sim.js';
import { resolveSurfaceGradient, offsetAsSurfaceGradient } from 'enginex/render/materials/SurfaceGradient.js'; // material models R4: THE NORMAL CONTRACT
import { laplacianBlendOn, laplacianPair, laplacianMode, LAPLACIAN_LEVELS, LAPLACIAN_MASK_S, materialModelStats } from './material_models.js'; // material models R4: the Laplacian transition
import { HEX_SCALE, HEX_EXP, HEX_LUM_FALLOFF, HEX_ROT, POM_SHADOW_STEPS } from './ground_shading.js'; // surfaces (2026-09-27): §2.3 hex-tiling, §2.4 parallax self-shadow
// ── turf ↔ grass (vegetation agent, visual-realism R2, 2026-09-25 session 2): the turf layer's colour IS the grass
//    layer's turf rule (lib/grass/turf_core.ts) over this vertex's tint / dryness / wetness, × the season tint ──
import { turfColorAtNode } from './grass/turf_color.js';
import { seasonTintNode } from './season_tint.js';
// ── end turf ↔ grass ──

const wetnessUniform = uniform(0);
// WEATHER SURFACES (shader gaps R3, 2026-09-30): the v2 ground's gloss (the surface FILM) and its puddles (standing water) —
// equal to the wetness unless THE ONE WET CLOCK (lib/wet_state.ts) drives them apart (the film goes first, the pores dry last).
const wetFilmUniform = uniform(0);
const wetPondUniform = uniform(0);
const snowUniform = uniform(0); // 0..1 — driven by WeatherModule while snowing
/** Blowing dust 0..1 and the wind it blows along (unit xz) — driven by WeatherModule in a sandstorm (2026-09-11). */
const dustUniform = uniform(0);
const dustWindUniform = uniform(new THREE.Vector2(1, 0));

export function applyWetness(material: THREE.MeshStandardMaterial): void {
  const m = material as unknown as { colorNode: unknown; roughnessNode: unknown; color: THREE.Color; roughness: number };
  m.colorNode = color(m.color).mul(mix(float(1.0), float(0.55), wetnessUniform)); // darken to 55%
  m.roughnessNode = float(m.roughness).mul(mix(float(1.0), float(0.3), wetnessUniform)); // glossier
}

/**
 * Wetness for a TEXTURED material (e.g. the terrain albedo). The plain `applyWetness`
 * builds its colorNode from `material.color` and IGNORES `.map` — so a textured surface
 * renders flat (bright white, when color was forced to white expecting the map to
 * colorize it). This variant samples the albedo map instead, so the texture SHOWS and
 * still darkens in rain. `texture(map)` with no uvNode auto-applies the map's
 * repeat/offset matrix (TextureNode.setUpdateMatrix), so the existing `map.repeat`
 * tiling is honored.
 */
export function applyWetnessTextured(material: THREE.MeshStandardMaterial, map: THREE.Texture): void {
  const m = material as unknown as { colorNode: unknown; roughnessNode: unknown; map: THREE.Texture; roughness: number };
  m.map = map; // keep the standard binding too (mipmaps / non-node fallback paths)
  m.colorNode = texture(map).mul(mix(float(1.0), float(0.55), wetnessUniform));
  m.roughnessNode = float(m.roughness).mul(mix(float(1.0), float(0.3), wetnessUniform));
}

/**
 * Multi-texture terrain ground (variety + anti-repetition). Blends three tiled ground
 * albedos — regolith (base), moss, and dirt — by two world-space perlin noises so the
 * surface breaks into organic patches instead of one obviously-repeating tile, plus a
 * low-frequency brightness variation. Still darkens in rain (shared wetness uniform).
 * The noises sample positionWorld so the patch layout is independent of the tile scale.
 */
export function applyTerrainGround(
  material: THREE.MeshStandardMaterial,
  regolith: THREE.Texture,
  moss: THREE.Texture,
  dirt: THREE.Texture,
): void {
  const m = material as unknown as { colorNode: unknown; roughnessNode: unknown; map: THREE.Texture; roughness: number };
  m.map = regolith;
  const a = texture(regolith), b = texture(moss), d = texture(dirt); // each tiles via its own .repeat
  const wp = positionWorld;
  // Moss/dirt patch masks from world-space noise (~50 m and ~20 m features), ramped to [0,1].
  const nMoss = mx_noise_float(vec3(wp.x.mul(0.02), wp.z.mul(0.02), 0), 0.5, 0.5);
  const nDirt = mx_noise_float(vec3(wp.x.mul(0.05).add(11.3), wp.z.mul(0.05).add(7.1), 0), 0.5, 0.5);
  const wMoss = clamp(nMoss.sub(0.50).mul(4.0), 0, 1);
  const wDirt = clamp(nDirt.sub(0.55).mul(4.0), 0, 1);
  const blended = mix(mix(a, b, wMoss), d, wDirt);
  // Mesh-matched shading (light version for personal worlds): steep eroded
  // faces shift toward desaturated exposed rock and roughen — gullies and
  // talus from the erosion bake read as stone, not draped dirt.
  const steep = smoothstep(float(0.82), float(0.55), normalWorld.y);
  const lum = blended.r.mul(0.2126).add(blended.g.mul(0.7152)).add(blended.b.mul(0.0722));
  const rockCol = vec3(lum.mul(0.62).add(0.10), lum.mul(0.60).add(0.10), lum.mul(0.58).add(0.11));
  const shaded = mix(blended.rgb, rockCol, steep.mul(0.7));
  // Large-scale (~170 m) brightness variation so even same-texture areas aren't uniform.
  const macro = mx_noise_float(vec3(wp.x.mul(0.006), wp.z.mul(0.006), 0), 0.15, 1.0); // ≈[0.85,1.15]
  m.colorNode = vec3(shaded.r, shaded.g, shaded.b).mul(macro).mul(mix(float(1.0), float(0.55), wetnessUniform));
  m.roughnessNode = float(m.roughness)
    .mul(steep.mul(0.15).add(1.0))
    .mul(mix(float(1.0), float(0.3), wetnessUniform));
}

/**
 * Personal-world ground WITH the Edit Mode paint layer: identical to
 * applyTerrainGround, but a per-vertex `paintW = [regolith, moss, dirt, mask]`
 * attribute overrides the noise patch blend where the owner painted. A
 * SEPARATE function (not a flag) so geometries without the attribute keep the
 * plain shader. WHY it must stay separate: a TSL attribute() the geometry
 * lacks is NOT a pipeline error — three warns at shader build and substitutes
 * a ZERO constant, and zero paint weights blend to black, so a shared shader
 * would silently black out every unpainted geometry.
 */
export function applyTerrainGroundPainted(
  material: THREE.MeshStandardMaterial,
  regolith: THREE.Texture,
  moss: THREE.Texture,
  dirt: THREE.Texture,
): void {
  const m = material as unknown as { colorNode: unknown; roughnessNode: unknown; map: THREE.Texture; roughness: number };
  m.map = regolith;
  const a = texture(regolith), b = texture(moss), d = texture(dirt);
  const wp = positionWorld;
  const nMoss = mx_noise_float(vec3(wp.x.mul(0.02), wp.z.mul(0.02), 0), 0.5, 0.5);
  const nDirt = mx_noise_float(vec3(wp.x.mul(0.05).add(11.3), wp.z.mul(0.05).add(7.1), 0), 0.5, 0.5);
  const wMoss = clamp(nMoss.sub(0.50).mul(4.0), 0, 1);
  const wDirt = clamp(nDirt.sub(0.55).mul(4.0), 0, 1);
  const natural = mix(mix(a, b, wMoss), d, wDirt);
  const pw = attribute('paintW', 'vec4'); // [regolith, moss, dirt, mask]
  const painted = a.mul(pw.x).add(b.mul(pw.y)).add(d.mul(pw.z));
  const blended = mix(natural, painted, pw.w);
  const steep = smoothstep(float(0.82), float(0.55), normalWorld.y);
  const lum = blended.r.mul(0.2126).add(blended.g.mul(0.7152)).add(blended.b.mul(0.0722));
  const rockCol = vec3(lum.mul(0.62).add(0.10), lum.mul(0.60).add(0.10), lum.mul(0.58).add(0.11));
  const shaded = mix(blended.rgb, rockCol, steep.mul(0.7));
  const macro = mx_noise_float(vec3(wp.x.mul(0.006), wp.z.mul(0.006), 0), 0.15, 1.0);
  m.colorNode = vec3(shaded.r, shaded.g, shaded.b).mul(macro).mul(mix(float(1.0), float(0.55), wetnessUniform));
  m.roughnessNode = float(m.roughness)
    .mul(steep.mul(0.15).add(1.0))
    .mul(mix(float(1.0), float(0.3), wetnessUniform));
}

/**
 * Biome-weighted ground shader for the Commons multi-biome terrain (Phase A).
 *
 * Reads the SIX biome weights baked per-vertex by `buildOneChunk` (via the exact JS
 * `biomeWeights(seed, wx, wz)` — same seeded hash used by foliage and the height profile)
 * from two custom vertex attributes: `biomeWA = [w0, w1, w2]` (regolith_plains, crystal_forest,
 * dune_desert) and `biomeWB = [w3, w4, w5]` (rocky_highlands, moss_wetland, fungal_basin).
 *
 * Because the weights are computed by the SAME JS function that drives the foliage and
 * terrain height, the ground color is always consistent with the plants/terrain on it.
 * City-pin collapse is already baked into the per-vertex weights — no TSL city-pin code.
 *
 * Keeps the existing macro low-frequency brightness variation and shared wetness darkening.
 *
 * @param material  A MeshStandardMaterial to wire TSL color/roughness nodes onto.
 * @param textures  One texture per biome (all six keys required, BIOMES order).
 */
export interface GroundFx {
  /** Deliot–Heitz stochastic sampling of the six near albedos (stage 4). */
  antiTiling?: boolean;
  /** Rain ripple rings on the composed normal (stage 4; the handle lands in rainHandles). */
  rainRipples?: boolean;
  /** Sample the roof map (lib/rain_shelter.ts): wetness, puddles, ripples and snow accumulation scale by the exposure (render wave 1). */
  rainShelter?: boolean;
  /** Specular AA (2026-09-15): Toksvig-filter the ground's roughness off the base biome normal map. */
  specularAA?: { varianceCap: number } | null;
}

/**
 * THE CLASSIC-TWIN KEY LAW (ground, 2026-09-26 — the root cause of session 2's "parallax has no effect"): the ground
 * materials are CLASSIC `MeshStandardMaterial`s carrying node graphs. three builds one through a node twin
 * (`NodeLibrary.fromMaterial`) but keys its render object by `material.customProgramCacheKey()` — the classic one
 * returns `onBeforeCompile`'s source text — plus the scalar properties, where a NODE-valued property keys as "{}"
 * (RenderObject.getMaterialCacheKey). Every ground material thus had ONE key, and three's node-builder cache handed
 * each rebuild the FIRST build's compiled graph AND bindings: parallax on/off, triplanar, the 1K / 2K sets and the
 * debug views never reached the screen (the probe measured |Δ| 0.03 and identical pictures), and the four far levels
 * all drew with the first level's bake and grid. Each graph now carries its own key.
 */
let groundGraphSerial = 0;
export function keyGroundGraph(material: THREE.Material, tag: string): string {
  const key = `${tag}#${++groundGraphSerial}`;
  (material as unknown as { customProgramCacheKey: () => string }).customProgramCacheKey = () => key;
  return key;
}

export function applyBiomeGround(
  material: THREE.MeshStandardMaterial,
  textures: Record<Biome, THREE.Texture>,
  normals?: Partial<Record<Biome, THREE.Texture>>,
  seaLevel?: number,
  quality: 'full' | 'simple' = 'full',
  fx: GroundFx = {},
): void {
  keyGroundGraph(material, 'ground-v1'); // THE CLASSIC-TWIN KEY LAW (above)
  const m = material as unknown as { colorNode: unknown; roughnessNode: unknown; normalNode: unknown; map: THREE.Texture; roughness: number };
  // Anchor .map for mipmaps / non-node fallback paths (regolith is the base biome).
  m.map = textures.regolith_plains;

  const wp = positionWorld;

  // ── Read per-vertex biome weights baked by buildOneChunk ─────────────────────
  // BIOMES order: [0] regolith_plains, [1] crystal_forest, [2] dune_desert (→ biomeWA)
  //              [3] rocky_highlands,  [4] moss_wetland,    [5] fungal_basin  (→ biomeWB)
  // The JS biomeWeights() guarantees they sum to 1 per vertex; linear interp across a
  // triangle may cause a tiny renorm drift (<0.01%) which is visually negligible.
  const wA = attribute('biomeWA', 'vec3'); // [w0, w1, w2]
  const wB = attribute('biomeWB', 'vec3'); // [w3, w4, w5]

  if (quality === 'simple') {
    // MINIMAL ground (2026-09-08): ONE albedo sample (the regolith base map)
    // tinted by the six per-vertex climate weights (each biome's mean colour),
    // the worn routes as the second and last sample, wetness as a multiply.
    // Two samplers instead of 37: no bands, no triplanar, no normals, no
    // puddles, no snow. The far tier wears the same material.
    const tint = vec3(1.0, 1.0, 1.0).mul(wA.x)        // regolith_plains — the sampled base
      .add(vec3(0.62, 0.86, 0.72).mul(wA.y))          // crystal_forest — cool green
      .add(vec3(1.18, 1.08, 0.82).mul(wA.z))          // dune_desert — warm sand
      .add(vec3(0.78, 0.78, 0.80).mul(wB.x))          // rocky_highlands — grey stone
      .add(vec3(0.60, 0.84, 0.52).mul(wB.y))          // moss_wetland — moss
      .add(vec3(0.82, 0.62, 0.86).mul(wB.z));         // fungal_basin — mauve
    const base = texture(textures[BIOMES[0]]).rgb.mul(tint);
    const roadWs = attribute('roadW', 'float');
    const road = texture(textures.rocky_highlands, uv().mul(1.7)).rgb.mul(0.8);
    const groundS = mix(base, road, roadWs);
    const macroS = mx_noise_float(vec3(wp.x.mul(0.006), wp.z.mul(0.006), 0), 0.15, 1.0);
    m.colorNode = groundS.mul(macroS).mul(mix(float(1.0), float(0.55), wetnessUniform));
    m.roughnessNode = float(m.roughness).mul(mix(float(1.0), float(0.58), wetnessUniform));
    return;
  }

  // ── Sample 6 albedo textures and blend by per-vertex biome weight ─────────────
  // `texture(map)` with no uvNode auto-applies the map's repeat/offset matrix.
  // BIOMES[i] order must match the packing order used in buildOneChunk.
  // Stage 4: with `antiTiling` each near albedo is sampled STOCHASTICALLY
  // (three jittered taps on a triangle lattice, variance-preserving blend —
  // Deliot & Heitz 2019) so the 22-repeat tiles never read as a grid; the
  // explicit uv reproduces the map's repeat the plain `texture(map)` applied.
  const near = (t: THREE.Texture) => {
    if (!fx.antiTiling) return texture(t);
    stage4Stats.stochasticGround++;
    return stochasticTexture(t, uv().mul(vec2(t.repeat.x || 1, t.repeat.y || 1)), { contrast: 1 }).node as ReturnType<typeof texture>;
  };
  let albedo = near(textures[BIOMES[0]]).mul(wA.x)     // regolith_plains
    .add(near(textures[BIOMES[1]]).mul(wA.y))           // crystal_forest
    .add(near(textures[BIOMES[2]]).mul(wA.z))           // dune_desert
    .add(near(textures[BIOMES[3]]).mul(wB.x))           // rocky_highlands
    .add(near(textures[BIOMES[4]]).mul(wB.y))           // moss_wetland
    .add(near(textures[BIOMES[5]]).mul(wB.z));          // fungal_basin

  // ── DISTANCE-BASED texture frequency blending ────────────────────────────────
  // One tile frequency can't look right both at your feet and 300 m out — the
  // 22-repeat tiles read as an obvious grid at distance. Beyond ~70 m the
  // climate blend cross-fades to a ~4.6-repeat (≈55 m tile) re-sample of the
  // SAME textures (explicit UV bypasses the repeat matrix; no new samplers).
  const camDist = length(cameraPosition.sub(wp));
  const farFade = smoothstep(float(70), float(260), camDist);
  const farUv = uv().mul(4.6);
  const albedoFar = texture(textures[BIOMES[0]], farUv).mul(wA.x)
    .add(texture(textures[BIOMES[1]], farUv).mul(wA.y))
    .add(texture(textures[BIOMES[2]], farUv).mul(wA.z))
    .add(texture(textures[BIOMES[3]], farUv).mul(wB.x))
    .add(texture(textures[BIOMES[4]], farUv).mul(wB.y))
    .add(texture(textures[BIOMES[5]], farUv).mul(wB.z));
  albedo = mix(albedo, albedoFar, farFade) as unknown as typeof albedo;

  // ── RAIN SHELTER (render wave 1, 2026-09-17): the roof map's exposure — 1 in
  //    the open, 0 under a porch, an awning, a canopy or a cave roof. The rain's
  //    every consequence on the ground scales by it; the permanent snow cap does not.
  const exposure = fx.rainShelter ? (shelterExposure(wp as never) as unknown as ReturnType<typeof float>) : float(1);
  const wetLocal = wetnessUniform.mul(exposure);
  const snowLocal = snowUniform.mul(exposure);

  // ── PUDDLE mask (doc §23): flat + depressed + rained-on → standing water.
  // Concavity proxy = low-frequency noise depressions (real curvature isn't
  // in the vertex data); gated hard on flatness so slopes shed. Fades in only
  // past moderate wetness — drizzle darkens, downpours pool.
  // 2026-09-11 ("small puddle effects when it rains"): two scales of hollows —
  // 20 m basins and 4 m pockets — so a rained-on plain shows scattered small
  // puddles, not one rare pool; they fill from moderate wetness up.
  const flatPow = clamp(normalWorld.y, 0, 1).pow(6);
  const basin = smoothstep(float(0.44), float(0.66), mx_noise_float(vec3(wp.x.mul(0.05), wp.z.mul(0.05), 0), 0.5, 0.5));
  const pocket = smoothstep(float(0.52), float(0.74), mx_noise_float(vec3(wp.x.mul(0.28).add(11.3), wp.z.mul(0.28).sub(4.7), 0), 0.5, 0.5));
  const depression = clamp(basin.mul(0.75).add(pocket.mul(0.6)), 0, 1);
  const puddle = flatPow.mul(depression).mul(smoothstep(float(0.25), float(0.7), wetLocal));

  // ── MESH-MATCHED bands (terrain-texturing goal): the surface follows the
  // GEOMETRY, not just the 270 km climate fields — beaches get sand, cliffs
  // and high ground get rock, peaks get snow, and plains break into grass
  // patches. All weights derive from positionWorld.y + normalWorld in-shader,
  // and the band palette REUSES the six bound biome textures (dune = sand,
  // rocky = stone, moss = grass) so the sampler budget is unchanged.
  const flatRaw = clamp(normalWorld.y, 0, 1);
  const steep = smoothstep(float(0.82), float(0.55), normalWorld.y);
  // Band edges wander ±~26 m at 250 m scale so they never read as contours.
  const elevJ = wp.y.add(mx_noise_float(vec3(wp.x.mul(0.004), wp.z.mul(0.004), 0), 26.0, 0.0));

  // Dry beach sand just above the waterline (the wet strip is handled below).
  const sandW = (seaLevel !== undefined
    ? float(1).sub(smoothstep(float(seaLevel + 2.0), float(seaLevel + 9.0), elevJ)).mul(0.9)
    : float(0)) as unknown as ReturnType<typeof float>;
  // Alpine exposed rock ramps in below the snowline; cliffs are rock anywhere.
  const rockHiW = smoothstep(float(240), float(320), elevJ);
  // Permanent snow cap — sheds from steep faces via the surface normal.
  const snowCapW = smoothstep(float(320), float(380), elevJ).mul(flatRaw.pow(0.7));
  const rockW = clamp(steep.mul(0.85).add(rockHiW.mul(float(1).sub(snowCapW))), 0, 1);
  // Grass patches over low, gentle plains — mid-scale noise variety so open
  // regolith country stops reading as one endless dirt tile.
  const grassNoise = clamp(
    mx_noise_float(vec3(wp.x.mul(0.012), wp.z.mul(0.012), 0), 0.5, 0.5).sub(0.42).mul(3.2), 0, 1);
  const grassW = grassNoise
    .mul(wA.x.add(wA.y).mul(0.75)) // only where plains/forest climate dominates
    .mul(float(1).sub(steep))
    .mul(float(1).sub(smoothstep(float(170), float(250), elevJ)));

  // ── Anti-flatness stack (engine terrain toolkit adoption) ────────────────────
  // 1. TRIPLANAR rock: planar XZ sampling smears cliff texture into streaks;
  //    project the SAME rock texture along X/Z on steep faces (no new
  //    samplers — same texture, extra UVs; flat ground keeps the planar path
  //    since the Y weight dominates there).
  const kTri = float(22 / 256); // match the planar repeat density (22 tiles / 256 m chunk)
  const triW = tslTriplanarWeights(float(4));
  const rockAlbedoTri = texture(textures.rocky_highlands, vec2(wp.z, wp.y).mul(kTri)).mul(triW.x)
    .add(texture(textures.rocky_highlands).mul(triW.y))
    .add(texture(textures.rocky_highlands, vec2(wp.x, wp.y).mul(kTri)).mul(triW.z));
  // 2. HEIGHT-AWARE band blending: linear mixes read as paint sprayed over
  //    paint; sharpen each transition by the layers' luminance (height proxy)
  //    so the brighter/rougher material pokes through the other (engine
  //    tslHeightBlend — returns the BASE layer's weight).
  const lumOf = (t: ReturnType<typeof texture>) =>
    t.r.mul(0.2126).add(t.g.mul(0.7152)).add(t.b.mul(0.0722));
  const baseLum = lumOf(albedo as never);
  const sharpen = (w: ReturnType<typeof float>, overlayLum: unknown) =>
    float(1).sub(tslHeightBlend(float(1).sub(w), w, baseLum, overlayLum, float(2)));
  const grassWS = sharpen(grassW as never, lumOf(texture(textures.moss_wetland) as never));
  const rockWS = sharpen(rockW as never, lumOf(rockAlbedoTri as never));
  const sandWS = sharpen(sandW as never, lumOf(texture(textures.dune_desert) as never));

  // Composite albedo: climate base → grass → rock → sand (snow lands later).
  let ground = mix(albedo, texture(textures.moss_wetland), grassWS);
  ground = mix(ground, rockAlbedoTri, rockWS);
  ground = mix(ground, texture(textures.dune_desert), sandWS);
  // Worn ROUTES (2026-08-31): the per-vertex road-surface mask paints packed
  // dirt (already a bound sampler — zero budget cost) darkened toward a used
  // track, exactly on the carriageways the settlement stamp carved.
  const roadW = attribute('roadW', 'float');
  ground = mix(ground, texture(textures.rocky_highlands, uv().mul(1.7)).mul(0.8), roadW);
  // Tilled FIELDS (Unbuilt Town wave): the fieldW channel paints worked soil
  // over the crop cells of each settlement's agricultural ring — same
  // zero-budget sampler-reuse trick as roadW, warmer and finer-grained so
  // plough soil reads apart from a trafficked track. Roads override fields.
  const fieldW = attribute('fieldW', 'float');
  ground = mix(ground, texture(textures.rocky_highlands, uv().mul(3.3)).mul(vec3(0.72, 0.58, 0.44)), fieldW.mul(roadW.oneMinus()));

  // ── Per-biome NORMAL maps (runtime-derived from the albedos) ─────────────────
  // Blend the six tangent-space normal textures by the SAME climate weights,
  // then re-mix by the SAME band weights as the albedo so bump detail follows
  // the visible surface (rock crags on cliffs, dune ripple on beaches). Snow
  // flattens the detail. normalMap's scale accepts a node, so the strength
  // itself is band-driven. (Bump mapping = this normal perturbation; specular
  // response = the roughnessNode below — full PBR surface control.)
  if (normals && BIOMES.every((b) => normals[b])) {
    let blended = texture(normals[BIOMES[0]]!).mul(wA.x)
      .add(texture(normals[BIOMES[1]]!).mul(wA.y))
      .add(texture(normals[BIOMES[2]]!).mul(wA.z))
      .add(texture(normals[BIOMES[3]]!).mul(wB.x))
      .add(texture(normals[BIOMES[4]]!).mul(wB.y))
      .add(texture(normals[BIOMES[5]]!).mul(wB.z));
    // Same SHARPENED weights as the albedo so bump detail follows the visible
    // surface; the rock normal reuses the triplanar projections.
    const rockNormalTri = texture(normals.rocky_highlands!, vec2(wp.z, wp.y).mul(kTri)).mul(triW.x)
      .add(texture(normals.rocky_highlands!).mul(triW.y))
      .add(texture(normals.rocky_highlands!, vec2(wp.x, wp.y).mul(kTri)).mul(triW.z));
    blended = mix(blended, texture(normals.moss_wetland!), grassWS) as unknown as typeof blended;
    blended = mix(blended, rockNormalTri, rockWS) as unknown as typeof blended;
    blended = mix(blended, texture(normals.dune_desert!), sandWS) as unknown as typeof blended;
    // 3. NEAR-FIELD micro detail (engine tslMicroDetailNormal, distance-faded
    //    10→55 m): procedural sub-metre grain whiteout-blended in tangent
    //    space — breaks the smooth close-up read without any extra texture.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TSL Fn tuple params type as 0-arg (same cast as the engine's own call sites)
    const microFade = (tslMicroStrength as any)(float(10), float(55));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const microUp = (tslMicroDetailNormal as any)(float(7), microFade.mul(0.85), float(0.35)); // Y-up (x, up, z) — 0.85 (was 0.5): close-up soil grain relief
    const microTangent = normalize(vec3(microUp.x, microUp.z, microUp.y));           // → tangent Z-up
    const unpacked = blended.rgb.mul(2).sub(1);
    const combined = tslWhiteoutNormalBlend(unpacked, microTangent);
    // Standing water flattens surface detail (doc §22: wetness reduces
    // porous normal detail; puddles read as a level micro-mirror).
    // 1.5 (was 0.8): the ground read as flat — rocks and soil grain need the
    // full relief ("amplify the bump mapping", 2026-08-20 field report).
    const nStrength = vec2(1.5, 1.5)
      .mul(float(1).sub(snowCapW.mul(0.7)))
      .mul(float(1).sub(puddle.mul(0.85)))
      .mul(mix(float(1.0), float(0.7), wetLocal));
    m.normalNode = normalMap(combined.mul(0.5).add(0.5), nStrength as unknown as ReturnType<typeof vec2>);
  }
  // Stage 4: raindrop rings over the composed normal — or the geometry normal
  // when no normal set is bound (up-facing only; the weather drives
  // `intensity` through rainHandles; `rainSkip` says why one declined).
  if (fx.rainRipples) {
    try { rainHandles.add(applyRainRipples(material as never, { intensity: 0, cellSize: 0.35, strength: 0.35, maskNode: fx.rainShelter ? exposure : undefined })); stage4Stats.rainMats++; }
    catch (e) { (stage4Stats as unknown as { rainSkip?: string }).rainSkip = String(e).slice(0, 160); }
  }

  // ── Macro brightness variation + wetness darkening (same as applyTerrainGround) ──
  const macro = mx_noise_float(vec3(wp.x.mul(0.006), wp.z.mul(0.006), 0), 0.15, 1.0); // ≈[0.85,1.15]
  // Mild residual cliff desaturation (the real rock TEXTURE now carries the
  // cliff look; this only adds tonal variance so faces read in flat light).
  const rockLum = ground.r.mul(0.2126).add(ground.g.mul(0.7152)).add(ground.b.mul(0.0722));
  const cliffColor = vec3(rockLum.mul(0.62).add(0.10), rockLum.mul(0.60).add(0.10), rockLum.mul(0.58).add(0.11));
  let shaded = mix(ground.rgb, cliffColor, steep.mul(0.3));
  // Snow: permanent elevation caps MERGED with weather accumulation
  // (flatness³ × snowiness) — one term, steep faces shed both. Procedural
  // snow albedo with a subtle sparkle noise (no extra sampler).
  const flatness = flatRaw.pow(3);
  const snowTotal = clamp(snowCapW.add(flatness.mul(snowLocal).mul(0.85)), 0, 1);
  const snowCol = vec3(0.90, 0.93, 0.985).mul(
    mx_noise_float(vec3(wp.x.mul(0.35), wp.z.mul(0.35), 0), 0.06, 1.0));
  shaded = mix(shaded, snowCol, snowTotal);
  // Shoreline band (terrain+water realism wave): ground near/below the sea
  // level grades into wet sand — the terrain half of the water's beach foam.
  // Purely height-driven (positionWorld.y), so it hugs every coast for free.
  const sandBand = (seaLevel !== undefined
    ? float(1).sub(smoothstep(float(seaLevel + 0.4), float(seaLevel + 3.0), wp.y))
    : float(0)) as unknown as ReturnType<typeof float>;
  if (seaLevel !== undefined) {
    shaded = mix(shaded, vec3(0.62, 0.55, 0.42), sandBand.mul(0.75));
  }
  // Puddles: darkened ground with a cool sky-ish cast (the IBL/specular does
  // the real reflecting once the roughness drops below).
  shaded = mix(shaded, shaded.mul(0.3).add(vec3(0.035, 0.05, 0.07)), puddle);
  // BLOWING SAND over the ground (a sandstorm): streaks of bright sand racing
  // downwind — anisotropic noise stretched 7:1 along the wind and scrolled at
  // ~30 m/s (Bagnold: blown sand moves within a metre of the surface, the
  // Journey / Uncharted "sand sheet" read), gated by the dust and by flatness.
  const sAlong = wp.x.mul(dustWindUniform.x).add(wp.z.mul(dustWindUniform.y));
  const sAcross = wp.x.mul(dustWindUniform.y.negate()).add(wp.z.mul(dustWindUniform.x));
  const sheetA = mx_noise_float(vec3(sAcross.mul(0.9), sAlong.mul(0.12).sub(time.mul(3.5)), 0), 0.5, 0.5);
  const sheetB = mx_noise_float(vec3(sAcross.mul(2.2).add(7.1), sAlong.mul(0.3).sub(time.mul(6.0)), 3.3), 0.5, 0.5);
  const drift = smoothstep(float(0.56), float(0.8), sheetA.mul(0.6).add(sheetB.mul(0.4))).mul(dustUniform).mul(flatRaw.pow(2));
  shaded = mix(shaded, vec3(0.86, 0.74, 0.5), drift.mul(0.6));
  m.colorNode = vec3(shaded.r, shaded.g, shaded.b).mul(macro).mul(mix(float(1.0), float(0.55), wetnessUniform));
  // Specular control (PBR roughness): micro-roughness from albedo luminance;
  // cliffs rougher, dry sand roughest, snow smoother (soft sheen), the wet
  // waterline strip glossy, and rain glosses everything.
  const lum = rockLum;
  // Gloss floors LIFTED (2026-08-20 "ground is way too shiny" report): wet
  // SOIL carries a sheen, not a mirror — the old 0.3 wet multiplier took the
  // whole landscape to roughness ≈ 0.26 in rain (and snow to 0.55). Mirror
  // gloss now belongs only to explicit PUDDLES.
  m.roughnessNode = float(m.roughness)
    .mul(lum.oneMinus().mul(0.3).add(0.8))
    .mul(steep.mul(0.15).add(1.0))
    .mul(mix(float(1.0), float(1.07), sandW))
    .mul(mix(float(1.0), float(0.74), snowTotal))
    .mul(mix(float(1.0), float(0.5), wetLocal))
    .mul(mix(float(1.0), float(0.62), sandBand))
    .mul(mix(float(1.0), float(0.1), puddle)); // puddles are a level mirror
  // Specular AA (2026-09-15): the Toksvig term reads the base biome's normal
  // map — the ground blends six and sets no `.normalMap`, so the regolith map
  // stands for the set. Composes over the roughness node built above; last.
  if (fx.specularAA) {
    try {
      specularAAHandles.add(applySpecularAA(material as never, { normalMapTexture: normals?.regolith_plains ?? null, varianceCap: fx.specularAA.varianceCap }));
      stage5Stats.specularAAMats++;
    } catch (e) { (stage5Stats as unknown as { specularAASkip?: string }).specularAASkip = String(e).slice(0, 160); }
  }
  // WATER AGENT (R7, 2026-09-25): ground under the sea is lit through the water column + caustics — lib/water_bed.ts (last; wraps the colour).
  if (seaLevel !== undefined) underwaterBedShading(material as never);
}

// ═══════════════════════════════════════════════════════════════════════════
// GROUND MATERIAL V2 (2026-09-25; research §7 R1 / R7 / R10 / R11)
// ═══════════════════════════════════════════════════════════════════════════
//
// Twelve scanned CC0 PBR layers in TWO KTX2 array textures (A = albedo +
// height, B = normal XY + roughness + AO — two samplers for any number of
// layers), placed per vertex by explicit rules (lib/ground_rules.ts: slope,
// two curvatures, a TWI-like wetness, water distance, altitude, forest,
// roads, fields — interleaved into ONE vertex buffer, lib/ground_layers.ts),
// blended by each layer's own HEIGHT (max(h_i + w_i) − 0.15, plus a ~2 m
// noise so transitions are sand in the cracks, not paint over paint), world-
// planar UVs with a double-precision origin offset (no `uv` attribute, no tile
// swimming at 1,000 km), three distance bands (3.5 m → 14 m → 56 m tiles,
// cross-faded), triplanar only on rock where it is steep, anti-tiling on the
// dominant layer, a 1.8 km macro map + a 224 m tap + a 2.5 km noise for the
// value / hue / dryness that survives distance.
//
// WGSL UNIFORMITY: a layer under 0.02 is skipped inside an `If`; every sample
// inside a branch is `textureSampleGrad` with gradients taken BEFORE the
// branch (implicit-derivative sampling in non-uniform control flow is a WGSL
// error; the gradient itself must be computed in uniform flow).
//
// ONE graph, several outputs: the colour Fn computes everything once and
// writes the view-space normal, roughness and AO into function-scope
// PROPERTY vars that the material's normal / roughness / ao nodes read.
// NodeMaterial builds the colour first (setupDiffuseColor → AO → variants →
// lighting, which asks for the normal lazily), so the vars are assigned
// before any read; the colour Fn must therefore NEVER read `normalWorld` /
// `normalView` (that would build the normal first) — it reads
// `normalWorldGeometry`, the interpolated vertex normal.

/** The FP64 origin, twice: modulo the tile period (precise UVs) and whole (noise / macro at km scales — integer metres, exact in float32). */
const uGroundOriginMod = uniform(new THREE.Vector2(0, 0));
const uGroundOrigin = uniform(new THREE.Vector2(0, 0));
/** Written by the rebase (lib/local_origin.ts): the origin is integer metres, so origin mod 1792 is exact in doubles. */
export function setGroundOrigin(ox: number, oz: number): void {
  const P = GROUND_TILE_PERIOD_M;
  (uGroundOriginMod as unknown as { value: THREE.Vector2 }).value.set(((ox % P) + P) % P, ((oz % P) + P) % P);
  (uGroundOrigin as unknown as { value: THREE.Vector2 }).value.set(ox, oz);
}
{ const a = renderAnchor(); setGroundOrigin(a.x, a.z); onRenderAnchor(setGroundOrigin); }

export interface GroundArrays { a: THREE.Texture; b: THREE.Texture; layers: number }
export interface GroundV2Opts {
  /** The two array textures; null (or quality 'simple') = the minimal per-vertex colour path. */
  arrays: GroundArrays | null;
  /** The 256² seamless macro-variation map (R value · G hue · B dry↔lush · A roughness). */
  macro: THREE.Texture | null;
  seaLevel?: number;
  quality?: 'full' | 'simple';
  /** The arrays hold the potato tier's 8 physical layers (lib/ground_layers.ts POTATO_FOLD). */
  potato?: boolean;
  /** Rock / strata projected along X and Z on steep faces. */
  triplanar?: boolean;
  /** IQ 2-fetch "virtual pattern" on the dominant layer. */
  antiTiling?: boolean;
  /** §2.3 (surfaces, 2026-09-27): with antiTiling, Mikkelsen's HEX-TILING (3 rotated fetches) replaces the IQ 2-fetch (high / ultra). */
  hexTiling?: boolean;
  /** §2.4 (surfaces, 2026-09-27): the parallax march's self-shadow (a second march toward the sun) — a flag, default off. */
  pomShadow?: boolean;
  /** 3 = near/mid/far tile bands; 2 = mid/far only (one cross-fade fewer). */
  bands?: 2 | 3;
  rainRipples?: boolean;
  rainShelter?: boolean;
  /** Toksvig on the blended layer normal. */
  specularAA?: boolean;
  /** R8c parallax occlusion on the top projection under POM_FADE_M: 'rock' = rock / strata / gravel (high), 'all' = every near layer (ultra). */
  pom?: 'off' | 'rock' | 'all';
  /** §2.1 MICRO-SHADOWING (surfaces, 2026-09-27): the B array's cavity AO shadows the DIRECT light (see GroundLightingModel). Default on. */
  microShadow?: boolean;
  /** Weather surfaces R3 (2026-09-30): read the deformation field (the shelter atlas's lower region) as an imprint. */
  deform?: boolean;
  /** Shader gaps R6 (2026-09-30): read the shallow-water puddle patch (lib/puddle_layer.ts — a storage buffer, WebGPU only). */
  puddleSim?: boolean;
  /** PROBE / DEBUG VIEW (ground, 2026-09-26 session 3; `__ground().debugView(mode)` rebuilds): the colour becomes a
   *  diagnostic × 0.25 (lit, so read channel RATIOS): 'pom' = (parallax fade, hit depth ÷ the layer's depth, the
   *  dominant layer's height at the base tap) — black where the march never runs; 'rock' = (rock, strata, gravel)
   *  weights. 'off' / absent emits nothing extra (the production graph is unchanged). */
  debugView?: 'off' | 'pom' | 'rock';
}

/** R8c: the parallax depth of each layer's height channel, metres (how far below the top a height of 0 lies). */
const POM_DEPTH_M: Record<GroundLayerKey, number> = {
  loam: 0.025, turf: 0.02, litter: 0.03, moss: 0.02, rock: 0.07, strata: 0.06, gravel: 0.045,
  beach: 0.015, dune: 0.015, mud: 0.02, snow: 0.015, alien: 0.03,
};
/** Parallax fades out between these camera distances (metres) — the near band only. */
export const POM_FADE_M: readonly [number, number] = [18, 25];
/** Height-march steps (plus the base tap) per mode. */
export const POM_STEPS = { rock: 8, all: 10 } as const;

/** Build facts for probes / the harness: which knobs the live ground material was built with, and its tap budget. */
export const groundV2Stats = {
  built: 0, quality: '' as string, physicalLayers: 0, triplanar: false, antiTiling: false, bands: 0,
  /** §2.3 (surfaces, 2026-09-27): the dominant layer's anti-tiling is Mikkelsen hex-tiling (3 fetches) rather than IQ's 2. */
  hexTiling: false,
  /** Worst-case array taps per pixel for 3 active layers, no band overlap (A + B per layer) and the macro taps. */
  tapsTypical: 0,
  /** Worst case: 3 layers in a band overlap on steep rock with anti-tiling. */
  tapsWorst: 0,
  /** R8c: 'off' | 'rock' | 'all', and the parallax march's taps per pixel under POM_FADE_M (0 when off). */
  pom: 'off' as string,
  pomTaps: 0,
  /** §2.4 (surfaces, 2026-09-27): the parallax self-shadow built in, and its extra taps per pixel under POM_FADE_M. */
  pomShadow: false,
  pomShadowTaps: 0,
};

const gNormalV = property('vec3', 'atlasGroundNV');
const gRough = property('float', 'atlasGroundRough');
const gAO = property('float', 'atlasGroundAO');
/** §2.4 (surfaces, 2026-09-27): the parallax self-shadow of the pixel toward the key light (1 = lit), read by the lighting model. */
const gPomSh = property('float', 'atlasGroundPomShadow');
/** §2.4: the world direction TO the key light (the sun, or the moon when it is the key) — EnvironmentModule feeds it every frame. */
const uGroundKey = uniform(new THREE.Vector3(0.3, 0.9, 0.2));
export function setGroundKeyDir(x: number, y: number, z: number): void { uGroundKey.value.set(x, y, z); }

/**
 * §2.1 MICRO-SHADOWING (surfaces, 2026-09-27; Brinck & Maximov, "The Technical Art of Uncharted 4", 2016; Lagarde's
 * course notes). The B array's AO is a CAVITY term, and three sends a material's `aoNode` to the INDIRECT light only
 * (PhysicalLightingModel.ambientOcclusion — the IBL / hemisphere diffuse, and Lagarde's specular occlusion on the IBL
 * specular): the SUN still lit the floor of every crack and the shadow side of every pebble. A cavity is a cone whose
 * opening (cos of its half-angle ≈ 1 − 2·ao²) the light must fall inside, so the direct term takes
 *     microShadow = saturate(N·L + 2·ao² − 1)
 * with N the GEOMETRY normal (the AO is baked against the flat tile) — 1 on open ground (ao 1) at any sun height, a
 * darkened crack floor under a low sun, the deepest texels shadowed even at noon. Applied in the LIGHTING MODEL's
 * direct term: every direct light (the sun, a campfire, a lamp), NEVER the albedo (that would darken the sky light and
 * the night too). HOW it reaches the classic ground material: three builds a classic material through a node twin
 * (NodeLibrary.fromMaterial) that copies every enumerable property (`for (const key in material)`), so an OWN
 * `setupLightingModel` on the classic material is the twin's. WHY not `material.receivedShadowNode` (the coordinator's
 * first hook): only ShadowNode.setup calls it — the sun's CSMShadowNode (LightingModule) never does, and a light that
 * casts no shadow (the fire lights, potato's sun) never reaches a shadow node at all.
 */
class GroundLightingModel extends THREE.PhysicalLightingModel {
  // §2.4 (surfaces, 2026-09-27): the same override carries the parallax self-shadow (gPomSh) when that knob is built in.
  constructor(private readonly micro = true, private readonly pomSh = false) { super(); }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- three's lighting-model inputs are loosely typed nodes
  direct(lightData: any, builder: any): void {
    let lc: N = lightData.lightColor;
    if (this.micro) {
      const nDotL: N = max(dot(normalViewGeometry, lightData.lightDirection), float(0));
      lc = lc.mul(clamp(nDotL.add(gAO.mul(gAO).mul(2)).sub(1), 0, 1));
    }
    if (this.pomSh) lc = lc.mul(gPomSh);
    super.direct({ ...lightData, lightColor: lc }, builder);
  }
}
/** Build facts: how many ground materials carry the micro-shadowing lighting model (probe / harness). */
export const groundMicroShadowStats = { models: 0 };

// §2.3 hex-tiling and §2.5 wetness by porosity: the rules and their CPU oracles live in lib/ground_shading.ts (surfaces, 2026-09-27).

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TSL node graphs are loosely typed; one alias keeps the v2 section readable
type N = any;

export function applyGroundV2(material: THREE.MeshStandardMaterial, o: GroundV2Opts): void {
  keyGroundGraph(material, 'ground-v2'); // THE CLASSIC-TWIN KEY LAW: each rebuild (knobs, set, debug view) its own graph + bindings
  const m = material as unknown as { colorNode: unknown; roughnessNode: unknown; normalNode: unknown; aoNode: unknown; map: THREE.Texture | null; roughness: number };
  const wp: N = positionWorld;
  const W0: N = attribute('gW0', 'vec4'), W1: N = attribute('gW1', 'vec4'), W2: N = attribute('gW2', 'vec4');
  const gTint: N = attribute('gTint', 'vec4'), gMask: N = attribute('gMask', 'vec4');
  const wLogical: N[] = [W0.x, W0.y, W0.z, W0.w, W1.x, W1.y, W1.z, W1.w, W2.x, W2.y, W2.z, W2.w];
  const tintRgb: N = gTint.xyz, dryness: N = gTint.w;
  const road: N = gMask.x, fieldM: N = gMask.y, wetM: N = gMask.z, routeD: N = gMask.w;
  const pUV: N = wp.xz.add(uGroundOriginMod);
  const pAbs: N = wp.xz.add(uGroundOrigin);
  const tintKOf = (key: GroundLayerKey): number => GROUND_LAYERS[LAYER_INDEX[key]].tintK;
  const tintFor = (key: GroundLayerKey): N => key === 'alien'
    ? tintRgb.mul(tintRgb).mul(tintRgb) // the alien layer IS its tint — cubed so crystal reads teal, fungal mauve
    : mix(vec3(1, 1, 1), tintRgb, float(tintKOf(key)));
  const macroAt = (tileM: number): N => (o.macro ? texture(o.macro, pUV.div(tileM)) : vec4(0.5, 0.5, 0.5, 0.5));
  const exposure: N = o.rainShelter ? (shelterExposure(wp as never) as unknown as N) : float(1);
  const wetLocal: N = wetnessUniform.mul(exposure);
  const filmLocal: N = wetFilmUniform.mul(exposure); // weather surfaces R3: the gloss and the puddles read the clock's film / pond
  const pondLocal: N = wetPondUniform.mul(exposure);
  // SHALLOW-WATER PUDDLES (shader gaps R6): decided at APPLY time (THE BUILD-TIME LAW) — the channel's buffer must exist
  const puddlePatch = o.puddleSim ? puddlePatchNow() : null;
  if (puddlePatch) ensurePuddleChannel(puddlePatch.n);
  const snowLocal: N = snowUniform.mul(exposure);
  const sea = o.seaLevel;

  groundV2Stats.built++;
  groundV2Stats.quality = o.quality === 'simple' || !o.arrays ? 'simple' : 'full';

  // ── MINIMAL: the rules' per-vertex colour (Σ w·mean·tint) + ONE macro tap ──
  if (o.quality === 'simple' || !o.arrays) {
    let col: N = vec3(0, 0, 0);
    let rough: N = float(0);
    for (let i = 0; i < GROUND_LAYER_COUNT; i++) {
      const k = GROUND_LAYER_KEYS[i];
      const mean = GROUND_LAYER_LOOK_MEAN[i]; // the look saturation (session 2) — the near shader's per-texel desaturation, on the mean
      // turf ↔ grass (vegetation agent, R2): the turf's per-vertex colour is the grass layer's turf rule (its mean).
      const layerCol: N = k === 'turf' ? turfColorAtNode(tintRgb, dryness, wetM).mul(seasonTintNode) : vec3(mean[0], mean[1], mean[2]).mul(tintFor(k));
      col = col.add(layerCol.mul(wLogical[i]));
      rough = rough.add(wLogical[i].mul(GROUND_LAYER_ROUGH[i]));
    }
    const mac: N = macroAt(GROUND_TILE_PERIOD_M);
    const value: N = float(1).add(mac.r.sub(0.5).mul(0.3));
    const colS: N = mix(col, col.mul(0.7), road.mul(0.6)).mul(float(1).sub(wetM.mul(0.2)));
    m.map = null;
    m.colorNode = colS.mul(value).mul(mix(float(1.0), float(0.55), wetnessUniform));
    m.roughnessNode = rough.mul(mix(float(1.0), float(0.58), wetnessUniform));
    groundV2Stats.physicalLayers = 0; groundV2Stats.tapsTypical = 1; groundV2Stats.tapsWorst = 1;
    applyDamageLayer(material, { mode: damageMode(), force: true, ground: true }); // shader gaps R2: last, over the whole graph (no sampler)
    return;
  }

  const arr = o.arrays;
  // Physical layers: 12, or the potato set with the logical weights FOLDED onto them.
  const phys: { key: GroundLayerKey; index: number; weight: N }[] = o.potato
    ? POTATO_PHYSICAL.map((key, index) => {
      let w: N = float(0);
      GROUND_LAYER_KEYS.forEach((lk, i) => { if (POTATO_FOLD[lk] === key) w = w.add(wLogical[i]); });
      return { key, index, weight: w };
    })
    : GROUND_LAYER_KEYS.map((key, index) => ({ key, index, weight: wLogical[index] }));
  const triOn = !!o.triplanar, atOn = !!o.antiTiling;
  const hexOn = atOn && !!o.hexTiling; // surfaces (2026-09-27): §2.3
  groundV2Stats.hexTiling = hexOn;
  const bands = o.bands ?? 3;
  groundV2Stats.physicalLayers = phys.length; groundV2Stats.triplanar = triOn; groundV2Stats.antiTiling = atOn; groundV2Stats.bands = bands;
  // FIX (shader sweep C, 2026-10-06): the full graph taps the macro map FOUR times (28 m, 7 m micro, 1.8 km, 224 m), not three.
  groundV2Stats.tapsTypical = 3 * 2 + (o.macro ? 4 : 0);
  groundV2Stats.tapsWorst = 3 * 2 * 2 * (hexOn ? 3 : atOn ? 2 : 1) + (triOn ? 2 * 2 * 2 : 0) + (o.macro ? 4 : 0);
  // The parallax facts are known NOW, from the options — the Fn body below runs only when the shader is BUILT
  // (the next frame that draws it), so stats written there lagged the material by a frame: the 2026-09-25 session-2
  // probe read 'off' right after the high overlay (pom 'rock') had built.
  {
    const pomModeNow = o.potato ? 'off' : (o.pom ?? 'off');
    const pomLayerCount = pomModeNow === 'all' ? phys.length : phys.filter((L) => L.key === 'rock' || L.key === 'strata' || L.key === 'gravel').length;
    groundV2Stats.pom = pomModeNow;
    groundV2Stats.pomTaps = pomModeNow === 'off' || !pomLayerCount ? 0 : POM_STEPS[pomModeNow] + 1;
    // §2.4 (surfaces, 2026-09-27): the self-shadow march rides the parallax — no parallax, no shadow march
    groundV2Stats.pomShadow = !!o.pomShadow && groundV2Stats.pomTaps > 0;
    groundV2Stats.pomShadowTaps = groundV2Stats.pomShadow ? POM_SHADOW_STEPS : 0;
  }
  const pomShOn = groundV2Stats.pomShadow; // §2.4
  // material models R4: the Laplacian pair, decided NOW (the Fn body below runs at shader build — a flag read there
  // would take whatever the flag says at the first render, not what this build was asked for)
  const pairKeys = laplacianPair();
  const lapA = phys.findIndex((L) => L.key === pairKeys[0]), lapB = phys.findIndex((L) => L.key === pairKeys[1]);
  const lapOn = laplacianBlendOn() && !o.potato && lapA >= 0 && lapB >= 0;
  if (lapOn) materialModelStats.laplacianGrounds++;
  const lapGaussOnly = laplacianMode() === 'gaussian';

  const groundFn = Fn(() => {
    const nG: N = normalWorldGeometry;
    // THE LAZY-VAR LAW (ground, 2026-09-26, read off the generated WGSL): a `.toVar()` that is never assigned again is
    // emitted where it is first USED — `dFdx(pUV).toVar('gdPx')` came out as `gdPx = dpdx(…)` INSIDE every layer's
    // `if (w > 0.02)` and the parallax `if`, i.e. derivatives in NON-UNIFORM control flow (three emits
    // `diagnostic(off, derivative_uniformity)`, so nothing complains; the value is undefined across a divergent quad —
    // wrong mips along every layer's edge). A var that is ASSIGNED is declared where it is made: the gradients are now
    // assigned here, once, in uniform flow, and every branch reads them.
    const dPx: N = vec2(0, 0).toVar('gdPx'), dPy: N = vec2(0, 0).toVar('gdPy');
    const dYx: N = float(0).toVar('gdYx'), dYy: N = float(0).toVar('gdYy');
    dPx.assign(dFdx(pUV)); dPy.assign(dFdy(pUV)); dYx.assign(dFdx(wp.y)); dYy.assign(dFdy(wp.y));
    const camDist: N = length(cameraPosition.sub(wp)).toVar('gCamD');
    // ── distance bands: A is the finer of the two scales in play, B the coarser; tB fades A → B ──
    const [f0, f1, f2, f3] = GROUND_BAND_FADE;
    const farSel: N = camDist.greaterThan(bands === 3 ? f2 : 0);
    const invSA: N = select(farSel, float(1 / 4), float(bands === 3 ? 1 : 1 / 4)).toVar('gInvSA');
    const invSB: N = select(farSel, float(1 / 16), float(bands === 3 ? 1 / 4 : 1 / 16)).toVar('gInvSB');
    const tB: N = select(farSel, smoothstep(float(f2), float(f3), camDist), smoothstep(float(f0), float(f1), camDist)).toVar('gTB');
    // ── the ~2 m ragged-edge noise and the anti-tiling index: the macro map at 28 m tiles (period / 64) ──
    const mSmall: N = macroAt(28);
    const n2: N = mSmall.g.sub(0.5).toVar('gN2');
    // ── triplanar weights from the GEOMETRY normal (|n|⁴), and how steep ──
    const ax: N = pow(abs(nG.x), float(4)), ay: N = pow(abs(nG.y), float(4)), az: N = pow(abs(nG.z), float(4));
    const aSum: N = max(ax.add(ay).add(az), float(1e-5));
    const triX: N = ax.div(aSum).toVar('gTriX'), triY: N = ay.div(aSum).toVar('gTriY'), triZ: N = az.div(aSum).toVar('gTriZ');
    const steepSel: N = smoothstep(float(0.9), float(0.8), nG.y).toVar('gSteep'); // n.y < 0.89 ⇔ slope > 0.5
    const sgnX: N = select(nG.x.greaterThanEqual(0), float(1), float(-1)).toVar('gSgnX');
    const sgnZ: N = select(nG.z.greaterThanEqual(0), float(1), float(-1)).toVar('gSgnZ');
    const negY: N = wp.y.negate();

    // One grad-sampled tap of an array layer (legal in non-uniform flow).
    const tap = (tex: THREE.Texture, layer: number, uvN: N, gx: N, gy: N): N => (texture(tex, uvN) as N).depth(int(layer)).grad(gx, gy);
    // IQ's "virtual pattern" (technique 3): two fetches at hashed offsets picked by a low-frequency index.
    const tapVariant = (tex: THREE.Texture, layer: number, uvN: N, gx: N, gy: N, dominant: N): N => {
      if (!atOn) return tap(tex, layer, uvN, gx, gy);
      const out: N = tap(tex, layer, uvN, gx, gy).toVar();
      If(dominant, () => {
        const k: N = n2.add(0.5).mul(8);
        const i: N = floor(k), f: N = fract(k);
        const offA: N = fract(sin(vec2(i.mul(113.1), i.mul(197.7))).mul(43758.5453));
        const offB: N = fract(sin(vec2(i.add(1).mul(113.1), i.add(1).mul(197.7))).mul(43758.5453));
        const cA: N = tap(tex, layer, uvN.add(offA), gx, gy), cB: N = tap(tex, layer, uvN.add(offB), gx, gy);
        const d: N = cA.xyz.sub(cB.xyz);
        out.assign(mix(cA, cB, smoothstep(float(0.2), float(0.8), f.sub(d.x.add(d.y).add(d.z).mul(0.1)))));
      });
      return out;
    };

    // §2.3 HEX-TILING (surfaces, 2026-09-27) — see HEX_SCALE. `kA` = the band's 1/tile (uv per metre): the period in uv is
    // 1792·kA (a whole number of tiles by the ground's tile law), × HEX_SCALE in lattice units (even: the (−P/2, P) shift
    // stays integral). `hw` carries the albedo pass's weights to the normal pass (`isB`), which rotates each normal back.
    const hash22 = (p: N): N => {
      const p3: N = fract(vec3(p.x, p.y, p.x).mul(vec3(0.1031, 0.1030, 0.0973))).toVar();
      p3.addAssign(dot(p3, p3.yzx.add(33.33)));
      return fract(vec2(p3.x, p3.x).add(p3.yz).mul(p3.zy));
    };
    const rot2 = (v: N, c: N, s: N): N => vec2(c.mul(v.x).sub(s.mul(v.y)), s.mul(v.x).add(c.mul(v.y)));
    const tapHex = (tex: THREE.Texture, layer: number, uvN: N, gx: N, gy: N, dominant: N, kA: N, rotK: number, hw: N, isB: boolean): N => {
      const out: N = tap(tex, layer, uvN, gx, gy).toVar();
      If(dominant, () => {
        const st: N = uvN.mul(HEX_SCALE);
        const sk: N = vec2(st.x.sub(st.y.mul(0.5)), st.y);
        const base: N = floor(sk);
        const fr: N = sk.sub(base);
        const tz: N = float(1).sub(fr.x).sub(fr.y);
        const s: N = select(tz.lessThan(0), float(1), float(0));
        const s2: N = s.mul(2).sub(1);
        const bary: N = vec3(tz.negate().mul(s2), s.sub(fr.y.mul(s2)), s.sub(fr.x.mul(s2)));
        const P: N = kA.mul(GROUND_TILE_PERIOD_M * HEX_SCALE);
        const verts: N[] = [base.add(vec2(s, s)), base.add(vec2(s, float(1).sub(s))), base.add(vec2(float(1).sub(s), s))];
        const cs: N[] = [];
        verts.forEach((v) => {
          // hexCanon: reduce the id into one period so the per-vertex random survives a rebase
          const ky: N = floor(v.y.add(0.5).div(P));
          const cy: N = v.y.sub(ky.mul(P));
          const x0: N = v.x.add(ky.mul(P).mul(0.5));
          const cx: N = x0.sub(floor(x0.add(0.5).div(P)).mul(P));
          const h: N = hash22(vec2(cx, cy));
          const a: N = hash22(vec2(cy.add(41.0), cx.add(17.0))).x.mul(2).sub(1).mul(Math.PI * rotK);
          const c: N = cos(a), sn: N = sin(a);
          const cen: N = vec2(v.x.add(v.y.mul(0.5)), v.y); // the vertex in st (the inverse skew)
          const uvI: N = rot2(st.sub(cen), c, sn).add(cen).div(HEX_SCALE).add(h);
          let tI: N = tap(tex, layer, uvI, rot2(gx, c, sn), rot2(gy, c, sn));
          if (isB) tI = vec4(rot2(tI.xy.mul(2).sub(1), c, sn).mul(0.5).add(0.5), tI.z, tI.w); // the tangent XY turns back
          cs.push(tI);
        });
        if (!isB) {
          const lw: N = vec3(0.299, 0.587, 0.114);
          const dw: N = mix(vec3(1, 1, 1), vec3(dot(cs[0].xyz, lw), dot(cs[1].xyz, lw), dot(cs[2].xyz, lw)), HEX_LUM_FALLOFF);
          const wRaw: N = dw.mul(pow(max(bary, vec3(1e-6, 1e-6, 1e-6)), vec3(HEX_EXP, HEX_EXP, HEX_EXP)));
          const wN: N = wRaw.div(max(wRaw.x.add(wRaw.y).add(wRaw.z), float(1e-6)));
          // Gain3, r 0.75 → k 2: x < ½ → ½(2x)², else 1 − ½(2 − 2x)²; renormalised
          const lo: N = wN.mul(2).mul(wN.mul(2)).mul(0.5);
          const hiB: N = vec3(2, 2, 2).sub(wN.mul(2));
          const hi: N = vec3(1, 1, 1).sub(hiB.mul(hiB).mul(0.5));
          const g: N = select(wN.x.lessThan(0.5), lo.x, hi.x);
          const g2: N = select(wN.y.lessThan(0.5), lo.y, hi.y);
          const g3: N = select(wN.z.lessThan(0.5), lo.z, hi.z);
          const gs: N = max(g.add(g2).add(g3), float(1e-6));
          hw.assign(vec3(g, g2, g3).div(gs));
        }
        out.assign(cs[0].mul(hw.x).add(cs[1].mul(hw.y)).add(cs[2].mul(hw.z)));
      });
      return out;
    };

    // ── R8c PARALLAX OCCLUSION (client-side: the engine POMNode wants a 2D height map and vertex
    //    tangents; the ground has an ARRAY and a world-planar frame, u → +X, v → +Z, n = +Y).
    //    The DOMINANT parallax layer's height (A.w) is marched along the view ray; the hit's
    //    horizontal shift (metres) moves EVERY top-projection tap, so the whole blend stands on
    //    one displaced surface. Faded by distance (POM_FADE_M), by steepness (the triplanar
    //    sides take over there) and by the layer's weight. All taps are grad-sampled with the
    //    gradients taken above (legal inside the If). ──
    const pomMode = o.potato ? 'off' : (o.pom ?? 'off');
    const pomLayers = pomMode === 'all' ? phys : phys.filter((L) => L.key === 'rock' || L.key === 'strata' || L.key === 'gravel');
    const pUVp: N = pUV.toVar('gPUVp');
    const dbgOn = !!o.debugView && o.debugView !== 'off';
    const dbg: N = dbgOn ? vec3(0, 0, 0).toVar('gDbg') : null;
    if (o.debugView === 'rock') dbg.assign(vec3(wLogical[LAYER_INDEX.rock], wLogical[LAYER_INDEX.strata], wLogical[LAYER_INDEX.gravel]));
    if (pomMode !== 'off' && pomLayers.length) {
      let domW: N = float(0), domIdx: N = float(0), domK: N = float(1), domD: N = float(0.05), sumW: N = float(0);
      for (const L of pomLayers) {
        const lp = GROUND_LAYERS[LAYER_INDEX[L.key]];
        const takes: N = L.weight.greaterThan(domW);
        domIdx = select(takes, float(L.index), domIdx);
        domK = select(takes, float(1 / lp.tileM), domK);
        domD = select(takes, float(POM_DEPTH_M[L.key]), domD);
        domW = max(domW, L.weight);
        sumW = sumW.add(L.weight);
      }
      const domIdxV: N = domIdx.toVar('gPomI'), domKV: N = domK.toVar('gPomK'), domDV: N = domD.toVar('gPomD');
      // The fade follows the parallax layers' COMBINED weight (session 2): the whole blend stands on the one displaced
      // surface, and on a rocky summit the rock splits into rock + strata (0.28 + 0.18 in the probe) — faded by the
      // dominant's 0.28 alone the shift was ~5 mm, the 9 taps paid for nothing (WebGPU on/off |Δ| 0.03).
      const pomWSum: N = sumW.toVar('gPomW');
      const pomW: N = float(1).sub(smoothstep(float(POM_FADE_M[0]), float(POM_FADE_M[1]), camDist))
        .mul(float(1).sub(steepSel)).mul(smoothstep(float(0.2), float(0.5), pomWSum)).toVar('gPomFade');
      const pomSV: N = pomShOn ? float(1).toVar('gPomSV') : null; // §2.4: 1 = lit (assigned inside the If, read after it)
      If(pomW.greaterThan(0.01), () => {
        const V: N = normalize(cameraPosition.sub(wp));
        const slide: N = V.xz.negate().div(max(V.y, float(0.25))); // metres of shift per metre of depth
        const kA: N = invSA.mul(domKV);
        const gx: N = dPx.mul(kA), gy: N = dPy.mul(kA);
        const layerI: N = int(domIdxV);
        const hAt = (off: N): N => (texture(arr.a, pUV.add(off).mul(kA)) as N).depth(layerI).grad(gx, gy).w;
        const steps = POM_STEPS[pomMode];
        // Step 0: the top. delta = rayDepth − surfaceDepth; the ray is under the surface once delta ≥ 0.
        const prevD: N = float(0).toVar('gPomPD');
        const h0: N = hAt(vec2(0, 0)).toVar('gPomH0');
        const prevDelta: N = float(0).sub(float(1).sub(h0).mul(domDV)).toVar('gPomPX');
        const hitD: N = select(prevDelta.greaterThanEqual(0), float(0), domDV).toVar('gPomHD');
        const found: N = select(prevDelta.greaterThanEqual(0), float(1), float(0)).toVar('gPomF');
        for (let i = 1; i <= steps; i++) {
          const d: N = domDV.mul(i / steps);
          const delta: N = d.sub(float(1).sub(hAt(slide.mul(d))).mul(domDV));
          const hitNow: N = found.lessThan(0.5).and(delta.greaterThanEqual(0));
          // Linear refinement between the last step above the surface and this one under it.
          const t: N = prevDelta.div(min(prevDelta.sub(delta), float(-1e-5)));
          hitD.assign(select(hitNow, mix(prevD, d, clamp(t, 0, 1)), hitD)); // t = prevΔ / (prevΔ − Δ) ∈ [0, 1]
          found.assign(select(hitNow, float(1), found));
          prevD.assign(d); prevDelta.assign(delta);
        }
        pUVp.assign(pUV.add(slide.mul(hitD).mul(pomW)));
        if (o.debugView === 'pom') dbg.assign(vec3(pomW, hitD.div(domDV), h0));
        // §2.4 PARALLAX SELF-SHADOW (surfaces, 2026-09-27; Tatarchuk 2006): from the hit, climb toward the key light over
        // POM_SHADOW_STEPS taps and keep the deepest intrusion of the height field into that ray — the CPU oracle is
        // ground_shading.pomSelfShadow (same steps, same ¼-depth softness, same 0.25 floor on the light's elevation).
        if (pomShOn) {
          const Lk: N = normalize(uGroundKey);
          const lsl: N = Lk.xz.div(max(Lk.y, float(0.25))); // metres sideways per metre of rise toward the light
          const hitOff: N = slide.mul(hitD);
          const occl: N = float(0).toVar('gPomSO');
          for (let k = 1; k <= POM_SHADOW_STEPS; k++) {
            const t = k / POM_SHADOW_STEPS;
            const rayD: N = hitD.mul(1 - t);
            const surfD: N = float(1).sub(hAt(hitOff.add(lsl.mul(hitD.mul(t))))).mul(domDV);
            occl.assign(max(occl, clamp(rayD.sub(surfD).div(domDV.mul(0.25)), 0, 1)));
          }
          const lit: N = Lk.y.greaterThan(0.05).and(hitD.greaterThan(0));
          pomSV.assign(select(lit, mix(float(1), float(1).sub(occl), pomW), float(1)));
        }
      });
      if (pomShOn) gPomSh.assign(pomSV);
    }
    // (groundV2Stats.pom / pomTaps are written outside the Fn — see above.)

    // ── PASS 1: albedo + height of every active layer (A array) ──
    const aVars: N[] = [], hVars: N[] = [];
    const hwVars: (N | null)[] = []; // §2.3 (surfaces, 2026-09-27)
    for (const L of phys) {
      const lp = GROUND_LAYERS[LAYER_INDEX[L.key]];
      const aV: N = vec4(0, 0, 0, 0).toVar(`gA${L.index}`);
      hwVars.push(hexOn ? vec3(1 / 3, 1 / 3, 1 / 3).toVar(`gHW${L.index}`) : null); // §2.3: the albedo pass's hex weights, reused by the normal pass
      const hV: N = float(-4).toVar(`gH${L.index}`);
      aVars.push(aV); hVars.push(hV);
      const kInv = 1 / lp.tileM;
      If(L.weight.greaterThan(0.02), () => {
        const kA: N = invSA.mul(kInv), kB: N = invSB.mul(kInv);
        const dom: N = L.weight.greaterThan(0.45);
        const t: N = (hexOn
          ? tapHex(arr.a, L.index, pUVp.mul(kA), dPx.mul(kA), dPy.mul(kA), dom, kA, HEX_ROT[L.key], hwVars[hwVars.length - 1], false)
          : tapVariant(arr.a, L.index, pUVp.mul(kA), dPx.mul(kA), dPy.mul(kA), dom)).toVar();
        If(tB.greaterThan(0.002), () => { t.assign(mix(t, tap(arr.a, L.index, pUVp.mul(kB), dPx.mul(kB), dPy.mul(kB)), tB)); });
        if (triOn && lp.triplanar) {
          If(steepSel.greaterThan(0.01), () => {
            const sx: N = tap(arr.a, L.index, vec2(pUV.y, negY).mul(kA), vec2(dPx.y, dYx.negate()).mul(kA), vec2(dPy.y, dYy.negate()).mul(kA));
            const sz: N = tap(arr.a, L.index, vec2(pUV.x, negY).mul(kA), vec2(dPx.x, dYx.negate()).mul(kA), vec2(dPy.x, dYy.negate()).mul(kA));
            const side: N = t.mul(triY).add(sx.mul(triX)).add(sz.mul(triZ));
            t.assign(mix(t, side, steepSel));
          });
        }
        aV.assign(t);
        hV.assign(t.w.mul(lp.heightScale).add(L.weight).add(n2.mul(0.16)));
      });
    }
    // Height-based splatting: every layer within 0.15 of the tallest (height + weight) takes part.
    let hMax: N = hVars[0];
    for (let i = 1; i < hVars.length; i++) hMax = max(hMax, hVars[i]);
    const cut: N = hMax.sub(0.15).toVar('gCut');

    // ── PASS 2: blend weights, colour, and the B array (normal, roughness, AO) of the layers that take part ──
    const col: N = vec3(0, 0, 0).toVar('gCol');
    const pert: N = vec3(0, 0, 0).toVar('gPert');
    const nAvg: N = vec3(0, 0, 0).toVar('gNAvg');
    const rough: N = float(0).toVar('gRoughAcc');
    const aoAcc: N = float(0).toVar('gAOAcc');
    const poroAcc: N = float(0).toVar('gPoroAcc'); // §2.5 (surfaces, 2026-09-27): the blend's porosity
    const bSum: N = float(0).toVar('gBSum');
    const hBlend: N = float(0).toVar('gHAcc');
    phys.forEach((L, idx) => {
      const lp = GROUND_LAYERS[LAYER_INDEX[L.key]];
      const kInv = 1 / lp.tileM;
      const b: N = max(hVars[idx].sub(cut), float(0));
      If(b.greaterThan(0.0005), () => {
        const kA: N = invSA.mul(kInv), kB: N = invSB.mul(kInv);
        let c: N;
        if (L.key === 'turf') {
          // ── turf ↔ grass (vegetation agent, visual-realism R2, 2026-09-25 session 2) ──
          // The turf layer is the meadow root-mat the grass stands on AND the meadow beyond the grass reach, so its
          // MEAN colour is the grass layer's turf rule (lib/grass/turf_core.ts) over this vertex's own tint, dryness
          // and wetness — the same three numbers the grass field reads off this chunk (grass_driver.groundAt) — and
          // the texture keeps only its detail (texel ÷ the layer's cooked mean). Replaces the tint mix + the straw
          // pull this layer had; its dryness now pulls through the rule's own straw. × the blades' season tint.
          const tm = GROUND_LAYER_MEAN[LAYER_INDEX.turf];
          c = aVars[idx].xyz.mul(turfColorAtNode(tintRgb, dryness, wetM).div(vec3(tm[0], tm[1], tm[2]))).mul(seasonTintNode);
          // ── end turf ↔ grass ──
        } else {
          // THE LOOK SATURATION (session 2): luminance-preserving, before the tint (ground_layers GROUND_LAYER_SAT).
          const satK = GROUND_LAYER_SAT[LAYER_INDEX[L.key]];
          const aRgb: N = aVars[idx].xyz;
          const aLook: N = satK < 1 ? mix(vec3(dot(aRgb, vec3(0.2126, 0.7152, 0.0722))), aRgb, float(satK)) : aRgb;
          c = aLook.mul(tintFor(L.key));
          if (L.key === 'loam' || L.key === 'litter') {
            // DRYNESS (R7): crests and the desert bleach toward straw.
            const lum: N = dot(c, vec3(0.2126, 0.7152, 0.0722));
            c = mix(c, vec3(lum.mul(1.18), lum.mul(1.02), lum.mul(0.62)), dryness.mul(0.3));
          }
        }
        col.addAssign(c.mul(b));
        hBlend.addAssign(aVars[idx].w.mul(b));
        const dom: N = L.weight.greaterThan(0.45);
        const t: N = (hexOn
          ? tapHex(arr.b, L.index, pUVp.mul(kA), dPx.mul(kA), dPy.mul(kA), dom, kA, HEX_ROT[L.key], hwVars[idx], true)
          : tapVariant(arr.b, L.index, pUVp.mul(kA), dPx.mul(kA), dPy.mul(kA), dom)).toVar();
        If(tB.greaterThan(0.002), () => { t.assign(mix(t, tap(arr.b, L.index, pUVp.mul(kB), dPx.mul(kB), dPy.mul(kB)), tB)); });
        // Top projection: u → +X, v → +Z and v runs DOWN the image, so the GL normal's green (up the image) is −Z.
        const tn: N = t.xy.mul(2).sub(1);
        const lPert: N = vec3(tn.x, 0, tn.y.negate()).toVar();
        if (triOn && lp.triplanar) {
          If(steepSel.greaterThan(0.01), () => {
            const sx: N = tap(arr.b, L.index, vec2(pUV.y, negY).mul(kA), vec2(dPx.y, dYx.negate()).mul(kA), vec2(dPy.y, dYy.negate()).mul(kA));
            const sz: N = tap(arr.b, L.index, vec2(pUV.x, negY).mul(kA), vec2(dPx.x, dYx.negate()).mul(kA), vec2(dPy.x, dYy.negate()).mul(kA));
            // Side projections use (h, −y): the image's up IS world up, so green is +Y; the horizontal axis flips with the face.
            const tx: N = sx.xy.mul(2).sub(1), tz: N = sz.xy.mul(2).sub(1);
            const px: N = vec3(0, tx.y, tx.x.mul(sgnX).negate());
            const pz: N = vec3(tz.x.mul(sgnZ), tz.y, 0);
            lPert.assign(mix(lPert, lPert.mul(triY).add(px.mul(triX)).add(pz.mul(triZ)), steepSel));
            t.assign(vec4(t.xy, mix(t.z, sx.z.mul(triX).add(sz.z.mul(triZ)).add(t.z.mul(triY)), steepSel), mix(t.w, sx.w.mul(triX).add(sz.w.mul(triZ)).add(t.w.mul(triY)), steepSel)));
          });
        }
        pert.addAssign(lPert.mul(b));
        nAvg.addAssign(normalize(nG.add(lPert)).mul(b));
        rough.addAssign(max(t.z, float(lp.roughMin)).mul(b));
        aoAcc.addAssign(t.w.mul(b));
        poroAcc.addAssign(b.mul(GROUND_LAYER_POROSITY[LAYER_INDEX[L.key]]));
        bSum.addAssign(b);
      });
    });
    // ── MATERIAL MODELS R4: THE LAPLACIAN TRANSITION (Wronski 2025) on ONE pair (LAPLACIAN_PAIR, picked on the bed).
    //    The height blend above crosses the pair's albedo over a narrow height ramp — it keeps the detail but steps
    //    the mean colour (the seam); a wide ramp washes the contrast. Here the pair's COARSER mips (grads × 2^k — the
    //    pixel's own pyramid, §6.1) cross over ramps that widen a level (Eq. 13 on the level-k HEIGHTS): the mean colour
    //    glides, the fine detail keeps the narrow ramp. Written as a correction to the blend above (engine
    //    LaplacianBlend.ts: Σ_k (f_k − f_0)·D_k — the telescoped Eq. 3), × the pair's share (LAPLACIAN_PAIR); the look saturation and
    //    tint are linear, so they apply to the level differences. Applied across the rules' transition (both vertex
    //    weights present), where the pair holds a share of the blend. Top projection only (faded on steep faces); each
    //    distance band gets the correction at its own tile scale, mixed by the band fade; albedo only — normals stay
    //    on the fine weights (the contract: gradients blend linearly), roughness linear. 2·LAPLACIAN_LEVELS coarser taps
    //    per active band, only inside the pair's transition. ──
    // (the flag and the pair are read at APPLY time, outside this Fn — its body runs when the shader is BUILT)
    if (lapOn) {
      const LA = phys[lapA], LB = phys[lapB];
      const bA: N = max(hVars[lapA].sub(cut), float(0)), bB: N = max(hVars[lapB].sub(cut), float(0));
      // THE ZONE is the rules' own transition (both vertex weights present), not the narrow band where both heights take
      // part: the coarse levels must reach PAST the height blend's ragged edge — that is where the mean colour steps
      If(LA.weight.greaterThan(0.02).and(LB.weight.greaterThan(0.02)).and(bA.add(bB).greaterThan(0.0005)).and(steepSel.lessThan(0.99)), () => {
        const pA = GROUND_LAYERS[LAYER_INDEX[LA.key]], pB = GROUND_LAYERS[LAYER_INDEX[LB.key]];
        const look = (L: typeof LA, rgb: N): N => {
          const satK = GROUND_LAYER_SAT[LAYER_INDEX[L.key]];
          const v: N = satK < 1 ? mix(vec3(dot(rgb, vec3(0.2126, 0.7152, 0.0722))), rgb, float(satK)) : rgb;
          return v.mul(tintFor(L.key));
        };
        const f0: N = bB.div(max(bA.add(bB), float(1e-4)));
        const n = LAPLACIAN_LEVELS;
        // the correction at one distance band's tile scale (`inv` = invSA or invSB): the levels ride the SAME pyramid
        // the band's own taps sample, so the near and the mid band each get their own; a band blend mixes the two
        const corrAt = (inv: N, tag: string): N => {
          const lvl = (L: typeof LA, k: number): N => {
            const kS: N = inv.mul(1 / GROUND_LAYERS[LAYER_INDEX[L.key]].tileM);
            return tap(arr.a, L.index, pUVp.mul(kS), dPx.mul(kS).mul(Math.pow(2, k)), dPy.mul(kS).mul(Math.pow(2, k)));
          };
          const a: N[] = [], b2: N[] = [];
          const k0 = lapGaussOnly ? n : 1; // 'gaussian': the mean colour alone (two taps), the detail keeps the height blend
          for (let k = k0; k <= n; k++) { a[k] = lvl(LA, k).toVar(`gLap${tag}A${k}`); b2[k] = lvl(LB, k).toVar(`gLap${tag}B${k}`); }
          let corr: N = vec3(0, 0, 0);
          for (let k = k0; k <= n; k++) {
            // the level-k mask (Wronski's Eq. 13 on the ground's own signal): the SMOOTH rule weights decide, the level-k
            // heights keep a share that falls to nothing at the Gaussian level ((n − k)/n), over a ramp 2^(k−1)·S wide —
            // the finest level keeps the height blend's ragged edge, the mean colour glides with the rules
            const hk = (n - k) / n;
            const dh: N = LB.weight.sub(LA.weight).add(b2[k].w.mul(pB.heightScale).sub(a[k].w.mul(pA.heightScale)).mul(hk));
            const fk: N = clamp(dh.div(LAPLACIAN_MASK_S * Math.pow(2, k - 1)).add(0.5), 0, 1);
            const dk: N = k < n
              ? look(LB, b2[k].xyz.sub(b2[k + 1].xyz)).sub(look(LA, a[k].xyz.sub(a[k + 1].xyz)))
              : look(LB, b2[k].xyz).sub(look(LA, a[k].xyz));
            corr = corr.add(dk.mul(fk.sub(f0)));
          }
          return corr;
        };
        const share: N = bA.add(bB).mul(float(1).sub(steepSel));
        If(tB.lessThan(0.998), () => { col.addAssign(corrAt(invSA, 'N').mul(share).mul(float(1).sub(tB))); });
        If(tB.greaterThan(0.002), () => { col.addAssign(corrAt(invSB, 'M').mul(share).mul(tB)); });
      });
    }
    const inv: N = float(1).div(max(bSum, float(1e-4)));
    const albedo: N = (lapOn ? max(col.mul(inv), vec3(0, 0, 0)) : col.mul(inv)).toVar('gAlbedo'); // §8.2: a Laplacian blend can undershoot — clamp
    const heightMix: N = hBlend.mul(inv);
    let roughness: N = rough.mul(inv);
    const aoMix: N = aoAcc.mul(inv);
    const poro: N = poroAcc.mul(inv).toVar('gPoro');

    // ── masks: ROADS (compacted), R11 RUTS on the regional routes, FIELDS (tilled) ──
    const flatRaw: N = clamp(nG.y, 0, 1);
    const onRoute: N = abs(routeD).lessThan(ROUTE_NONE - 0.1).and(road.greaterThan(0.05));
    const gauge: N = float(0.78).mul(float(1).add(n2.mul(0.16))); // width jitter along the track
    const ad: N = abs(routeD);
    const rut: N = select(onRoute, float(1).sub(smoothstep(float(0.06), float(0.24), abs(ad.sub(gauge)))).mul(float(1).sub(smoothstep(float(1.8), float(2.6), ad))), float(0)).toVar('gRut');
    const crown: N = select(onRoute, float(1).sub(smoothstep(float(0.1), float(0.45), ad)), float(0));
    let shaded: N = mix(albedo, albedo.mul(0.82), road.mul(0.7));        // compacted track
    shaded = mix(shaded, shaded.mul(0.72), rut.mul(road));               // the wheel ruts
    shaded = mix(shaded, shaded.mul(1.06), crown.mul(road).mul(0.6));     // the dry crown
    // 0.875 m furrows on the ORIGIN-MOD coordinate: 1792 / 0.875 = 2048, so a rebase never moves them, and at
    // 1,000 km the absolute coordinate's 6 cm float32 step would have banded a 0.9 m sine into 15 stairs.
    const furrow: N = sin(pUV.x.mul(Math.PI * 2 / 0.875)).mul(0.5).add(0.5);
    shaded = mix(shaded, shaded.mul(vec3(0.78, 0.66, 0.52)).mul(furrow.mul(0.16).add(0.9)), fieldM.mul(0.85));
    roughness = roughness.mul(mix(float(1), float(0.86), road));

    // ── PUDDLES: hollows (the rules' wet mask × small noise basins) + the ruts, filling as it rains ──
    const flatPow: N = flatRaw.pow(6);
    const basin: N = smoothstep(float(0.44), float(0.66), mx_noise_float(vec3(pAbs.x.mul(0.05), pAbs.y.mul(0.05), 0), 0.5, 0.5));
    const pocket: N = smoothstep(float(0.52), float(0.74), mx_noise_float(vec3(pAbs.x.mul(0.28).add(11.3), pAbs.y.mul(0.28).sub(4.7), 0), 0.5, 0.5));
    // PERF (shader sweep C, 2026-10-06): the hollows' two Perlin noises reach the picture only through
    // smoothstep(0.25, 0.7, pondLocal), which is EXACTLY 0 while the pond clock is ≤ 0.25 (pondLocal ≤ wetPondUniform:
    // the shelter's exposure is ≤ 1) — i.e. on every dry day. Gate them on the UNIFORM; the sim patch's relief reads
    // them whatever the clock says, so with the patch on they stay ungated (same graph, node for node).
    const hollowOf = (): N => basin.mul(0.6).add(pocket.mul(0.5));
    const hollow: N = puddlePatch ? hollowOf() : float(0).toVar('gHollow');
    if (!puddlePatch) If(wetPondUniform.greaterThan(0.25), () => { hollow.assign(hollowOf()); });
    const depression: N = clamp(hollow.mul(wetM.mul(0.8).add(0.35)).add(float(1).sub(heightMix).mul(0.15)), 0, 1);
    const puddle: N = max(flatPow.mul(depression).mul(smoothstep(float(0.25), float(0.7), pondLocal)),
      rut.mul(road).mul(flatRaw.pow(3)).mul(smoothstep(float(0.2), float(0.6), pondLocal))).toVar('gPuddle');
    // ── SHALLOW-WATER PUDDLES (shader gaps R6, lib/puddle_sim.ts): ONE puddle system, two layers. Inside the sim patch the
    //    puddle reads ONLY the sim (its water level against THIS fragment's ground: the drawn height minus the same
    //    micro-relief the sim's bed lost — the hollows above without their texture term, the ruts — and, in the look
    //    only, the texture's own crevices, the static depression's texture term as a depth); over the patch's last
    //    HANDOVER_M it blends to the static fill above. Uniform flow, four storage loads, no sampler. ──
    let simWet: N = null; // absent when the patch is off: the graph is the static one, node for node
    if (puddlePatch) {
      const reliefM: N = clamp(basin.mul(0.6).add(pocket.mul(0.5)).mul(wetM.mul(0.8).add(0.35)), 0, 1).mul(RELIEF_M).add(rut.mul(road).mul(RUT_M))
        .add(float(1).sub(heightMix).mul(LOOK_TEX_RELIEF * RELIEF_M));
      const pt = puddleGroundTerm(wp, reliefM);
      puddle.assign(mix(puddle, pt.cover, pt.weight));
      simWet = pt.wet.mul(pt.weight).toVar('gSimWet');
    }

    // ── the normal: layers + micro detail under 10 m, flattened by snow / puddles / rain ──
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- the engine's TSL Fn tuple params type as 0-arg
    const microFade: N = (tslMicroStrength as any)(float(4), float(10));
    // The micro normal from the macro map at a 7 m tile (1792 / 7 = 256 — periodic under the origin law). The
    // engine's tslMicroDetailNormal hashes RENDER-space positionWorld, so its pattern jumped at every rebase.
    const mMicro: N = macroAt(7);
    const micro: N = vec3(mMicro.r.sub(0.5), 0, mMicro.g.sub(0.5)).mul(microFade.mul(0.45));
    const flatten: N = float(1).sub(puddle.mul(0.9)).mul(float(1).sub(road.mul(0.35))).mul(mix(float(1), float(0.7), filmLocal));
    const snowAcc: N = clamp(flatRaw.pow(3).mul(snowLocal).mul(0.85), 0, 1);
    // SNOW AND MUD THAT REMEMBER (weather surfaces R3): the deformation field's imprint — how much of a dent shows is
    // the surface's (snow all, sand most, wet soil some, roads / rock none); no displacement (5.3 m vertices), no sampler.
    const W = (k: GroundLayerKey): N => wLogical[LAYER_INDEX[k]];
    const dfm = o.deform ? deformImprint(wp.x, wp.z, {
      snow: max(snowAcc, W('snow')), soil: W('loam').add(W('turf')).add(W('litter')).add(W('mud').mul(1.6)),
      sand: W('dune').add(W('beach')), hard: road.add(W('rock')).add(W('strata')).add(W('gravel')), wet: wetLocal,
    }) : null;
    if (dfm) puddle.assign(max(puddle, dfm.rutWater));
    const nPert: N = pert.mul(inv).add(vec3(micro.x, 0, micro.z)).mul(flatten).mul(float(1).sub(snowAcc.mul(0.7)));
    // THE NORMAL CONTRACT (material models R4): the layers, the micro normal and the imprint are ONE surface gradient
    // (the offset form — UDN-style world vectors, as before) resolved ONCE against the geometry normal: normalize(nG + v)
    const nW: N = resolveSurfaceGradient(nG, offsetAsSurfaceGradient(dfm ? nPert.mul(1.25).add(dfm.pert) : nPert.mul(1.25)));
    materialModelStats.routedGround++;
    gNormalV.assign(normalize(cameraViewMatrix.mul(vec4(nW, 0)).xyz));

    // ── SNOW (weather accumulation on flat ground; the permanent caps are the snow layer) ──
    // PERF (shader sweep C, 2026-10-06): snowAcc carries snowUniform, so with no snow the mix weight is exactly 0 and the
    // sparkle noise never reaches the picture — it ran on every ground fragment anyway. Gated on the uniform (x·0 = 0).
    const snowN: N = float(1).toVar('gSnowN');
    If(snowUniform.greaterThan(0), () => { snowN.assign(mx_noise_float(vec3(pAbs.x.mul(0.35), pAbs.y.mul(0.35), 0), 0.06, 1.0)); });
    const snowCol: N = vec3(0.90, 0.93, 0.985).mul(snowN);
    shaded = mix(shaded, snowCol, snowAcc);
    if (dfm) shaded = dfm.shade(shaded); // compacted snow a touch greyer, a rut darker (weather surfaces R3)
    // ── WET BANKS (R10): permanently wet ground is darker and glossier ──
    shaded = mix(shaded, shaded.mul(0.78), wetM.mul(0.6));
    // Shoreline: the last metres above the sea are wet sand.
    const sandBand: N = sea !== undefined ? float(1).sub(smoothstep(float(sea + 0.4), float(sea + 3.0), wp.y)) : float(0);
    // THE WET SAND BAND darkens by HALF (2026-09-27, the coordinator's review; was ×0.82): Jensen 1999 measures a
    // wet sand's albedo at ~½ its dry value (water fills the pores, light enters and leaves through the film's total
    // internal reflection) — the strip the tide reaches reads dark and glossy against the dry beach, 0.4–3 m up.
    // §2.5 (surfaces, 2026-09-27): the band reads the POROSITY — sand keeps the coordinator's ×(0.55, 0.53, 0.5), a rocky
    // shore darkens little (it glosses instead — the roughness below): groundWetBand, × the band's slight cool shift.
    if (sea !== undefined) shaded = mix(shaded, shaded.mul(vec3(1.04, 1.0, 0.94).mul(mix(float(1), float(0.53), min(poro.div(0.9), float(1))))), sandBand.mul(0.85));
    // Puddles: dark with a cool sky cast (the IBL does the real reflecting once the roughness drops).
    shaded = mix(shaded, shaded.mul(0.3).add(vec3(0.035, 0.05, 0.07)), puddle);
    // Blowing sand sheets (the sandstorm), as v1.
    // PERF (shader sweep C, 2026-10-06): drift carries dustUniform, which is 0 outside a sandstorm — yet the two sheet
    // noises ran on every ground fragment of every tier. Gated on the UNIFORM: the same expression when it blows, 0 when not.
    const drift: N = float(0).toVar('gDrift');
    If(dustUniform.greaterThan(0), () => {
      const sAlong: N = pAbs.x.mul(dustWindUniform.x).add(pAbs.y.mul(dustWindUniform.y));
      const sAcross: N = pAbs.x.mul(dustWindUniform.y.negate()).add(pAbs.y.mul(dustWindUniform.x));
      const sheetA: N = mx_noise_float(vec3(sAcross.mul(0.9), sAlong.mul(0.12).sub(time.mul(3.5)), 0), 0.5, 0.5);
      const sheetB: N = mx_noise_float(vec3(sAcross.mul(2.2).add(7.1), sAlong.mul(0.3).sub(time.mul(6.0)), 3.3), 0.5, 0.5);
      drift.assign(smoothstep(float(0.56), float(0.8), sheetA.mul(0.6).add(sheetB.mul(0.4))).mul(dustUniform).mul(flatRaw.pow(2)));
    });
    shaded = mix(shaded, vec3(0.86, 0.74, 0.5), drift.mul(0.6));

    // ── MACRO variation that survives distance: the 1.8 km map, a 224 m tap, a 2.5 km noise ──
    const mFar: N = macroAt(GROUND_TILE_PERIOD_M), mMid: N = macroAt(224);
    const km: N = mx_noise_float(vec3(pAbs.x.mul(1 / 2500), pAbs.y.mul(1 / 2500), 0), 0.5, 0.5);
    const value: N = float(1).add(mFar.r.sub(0.5).mul(0.22)).add(mMid.r.sub(0.5).mul(0.14)).add(km.sub(0.5).mul(0.12));
    const hue: N = mix(vec3(1.04, 1.0, 0.94), vec3(0.95, 1.0, 1.05), mFar.g.mul(0.6).add(mMid.g.mul(0.4)));
    const lumS: N = dot(shaded, vec3(0.2126, 0.7152, 0.0722));
    const sat: N = mix(float(0.88), float(1.1), mMid.b.mul(0.5).add(mFar.b.mul(0.5)));
    shaded = mix(vec3(lumS, lumS, lumS), shaded, sat).mul(hue).mul(value);
    roughness = roughness.mul(mFar.a.sub(0.5).mul(0.16).add(1));

    // ── WATER AGENT INSERTION POINT: underwaterBedShading(shaded, wp) goes here (the lake/sea bed under water) ──

    // Wetness: rain darkens and glosses (wet SOIL is a sheen, not a mirror; puddles are the mirror).
    // §2.5 (surfaces, 2026-09-27): BY POROSITY — groundWetDarkening / groundWetRoughness (loam keeps the old ×0.55 / ×0.5).
    const finalCol: N = shaded.mul(mix(float(1.0), mix(float(1), float(0.4), poro), wetLocal));
    let r2: N = roughness
      .mul(mix(float(1.0), poro.mul(0.3).add(0.3), filmLocal))
      .mul(mix(float(1.0), float(0.65), wetM.mul(0.7)))
      .mul(mix(float(1.0), float(0.74), snowAcc))
      .mul(mix(float(1.0), float(0.62), sandBand))
      .mul(mix(float(1.0), float(0.1), puddle));
    // R6: a film the sim holds reads wet where the clock's own has dried — max(film, sim) as a ratio on the line above
    if (simWet) r2 = r2.mul(mix(float(1.0), poro.mul(0.3).add(0.3), max(filmLocal, simWet)).div(mix(float(1.0), poro.mul(0.3).add(0.3), filmLocal)));
    if (o.specularAA) {
      // Toksvig on the blended layer normal: divergent layer normals shorten their average → rougher, no sparkle.
      const len: N = clamp(length(nAvg.mul(inv)), 0.2, 1);
      const variance: N = min(float(1).sub(len).div(len), float(0.35));
      r2 = sqrt(r2.mul(r2).add(variance));
    }
    gRough.assign(clamp(dfm ? r2.mul(dfm.rough) : r2, 0.04, 1));
    gAO.assign(dfm ? mix(float(1), aoMix, 0.75).mul(float(1).sub(rut.mul(road).mul(0.25))).mul(dfm.ao) : mix(float(1), aoMix, 0.75).mul(float(1).sub(rut.mul(road).mul(0.25))));
    // R6: and darkens as max(absorbed, sim) — the same ratio form on the final colour
    const simDark: N = simWet ? mix(float(1.0), mix(float(1), float(0.4), poro), max(wetLocal, simWet)).div(mix(float(1.0), mix(float(1), float(0.4), poro), wetLocal)) : null;
    return dbgOn ? dbg.mul(0.25) : (simDark ? finalCol.mul(simDark) : finalCol);
  });

  m.map = null;
  m.colorNode = groundFn();
  m.roughnessNode = gRough;
  m.normalNode = gNormalV;
  m.aoNode = gAO;
  // §2.1 MICRO-SHADOWING (surfaces, 2026-09-27): the twin copies this OWN property (see GroundLightingModel).
  // §2.4: the same lighting model carries the parallax self-shadow when it is built in (pomShOn) — micro-shadow off alone
  // still needs the model for the parallax shadow.
  const microOn = o.microShadow !== false;
  if (microOn || pomShOn) {
    (material as unknown as { setupLightingModel: () => unknown }).setupLightingModel = () => { groundMicroShadowStats.models++; return new GroundLightingModel(microOn, pomShOn); };
  } else delete (material as unknown as { setupLightingModel?: unknown }).setupLightingModel;
  if (o.rainRipples) {
    try { rainHandles.add(applyRainRipples(material as never, { intensity: 0, cellSize: 0.35, strength: 0.35, maskNode: o.rainShelter ? exposure : undefined })); stage4Stats.rainMats++; }
    catch (e) { (stage4Stats as unknown as { rainSkip?: string }).rainSkip = String(e).slice(0, 160); }
  }
  // WATER AGENT (R7, 2026-09-25): ground under the sea is lit through the water column + caustics — lib/water_bed.ts (last; wraps the colour, builds it first).
  if (o.seaLevel !== undefined) underwaterBedShading(material as never);
  applyDamageLayer(material, { mode: damageMode(), force: true, ground: true }); // shader gaps R2: last, over the whole graph (no sampler — the budget is spent)
}

/**
 * R7 — the FAR TIER's ground (v2): per level a baked macro albedo (the same
 * rules, the layers' means, the tints; rock exposure, snow, dryness) and a
 * normal map at 2× the level's grid (lib/ground_far.ts, the far worker), so
 * ridges and gullies light at 5–50 km. Per-level uniforms place the texel
 * grid; the macro map and the 2.5 km noise keep the distance from reading as
 * one flat bake. Weather (rain darkening, snow, sand) composes as on the chunks.
 */
export interface FarGroundHandles { albedo: { value: THREE.Texture }; normal: { value: THREE.Texture }; grid: { value: THREE.Vector4 } }
export function applyGroundFar(material: THREE.MeshStandardMaterial, albedoTex: THREE.Texture, normalTex: THREE.Texture, grid: THREE.Vector4, macro: THREE.Texture | null): FarGroundHandles {
  keyGroundGraph(material, 'ground-far'); // THE CLASSIC-TWIN KEY LAW: every far level binds ITS OWN bake + grid
  const m = material as unknown as { colorNode: unknown; roughnessNode: unknown; normalNode: unknown; map: THREE.Texture | null };
  const wp: N = positionWorld;
  const pAbs: N = wp.xz.add(uGroundOrigin);
  const pUV: N = wp.xz.add(uGroundOriginMod);
  const uGrid = uniform(grid); // x0, z0, step, texN
  const albedoNode: N = texture(albedoTex);
  const normalNode: N = texture(normalTex);
  const gv: N = uGrid;
  const uvTex: N = vec2(pAbs.x.sub(gv.x).div(gv.z).add(0.5).div(gv.w), pAbs.y.sub(gv.y).div(gv.z).add(0.5).div(gv.w));
  const a: N = albedoNode.sample(uvTex);
  const nt: N = normalNode.sample(uvTex);
  const nx: N = nt.x.mul(2).sub(1), nz: N = nt.y.mul(2).sub(1);
  const nW: N = normalize(vec3(nx, sqrt(max(float(1).sub(nx.mul(nx)).sub(nz.mul(nz)), float(0.02))), nz));
  const mFar: N = macro ? texture(macro, pUV.div(GROUND_TILE_PERIOD_M)) : vec4(0.5, 0.5, 0.5, 0.5);
  const km: N = mx_noise_float(vec3(pAbs.x.mul(1 / 2500), pAbs.y.mul(1 / 2500), 0), 0.5, 0.5);
  let col: N = a.xyz.mul(float(1).add(mFar.r.sub(0.5).mul(0.2)).add(km.sub(0.5).mul(0.14)));
  const flat: N = clamp(nW.y, 0, 1);
  col = mix(col, vec3(0.90, 0.93, 0.985), clamp(flat.pow(3).mul(snowUniform).mul(0.85), 0, 1));
  m.map = null;
  m.colorNode = col.mul(mix(float(1.0), float(0.55), wetnessUniform));
  m.roughnessNode = clamp(a.w.mul(mix(float(1.0), float(0.55), wetnessUniform)), 0.05, 1);
  m.normalNode = normalize(cameraViewMatrix.mul(vec4(nW, 0)).xyz);
  return { albedo: albedoNode as unknown as { value: THREE.Texture }, normal: normalNode as unknown as { value: THREE.Texture }, grid: uGrid as unknown as { value: THREE.Vector4 } };
}

/** Snow accumulation layers on props/structures (engine AccumulationLayer handles) — the snow uniform drives their coverage. */
export const accumulationHandles = new Set<{ coverage: { value: number } }>();
export function setSnowiness(v: number): void { for (const h of accumulationHandles) h.coverage.value = THREE.MathUtils.clamp(v, 0, 1); (snowUniform as unknown as { value: number }).value = THREE.MathUtils.clamp(v, 0, 1); }
export function setWetness(v: number): void {
  const c = THREE.MathUtils.clamp(v, 0, 1);
  (wetnessUniform as unknown as { value: number }).value = c;
  (wetFilmUniform as unknown as { value: number }).value = c; // one number, as before (the clock off)
  (wetPondUniform as unknown as { value: number }).value = c;
}
/** THE ONE WET CLOCK (weather surfaces R3): the soil's absorbed water (darkening), its film (gloss) and its ponded water (puddles). */
export function setWetLevels(absorbed: number, film: number, pond: number): void {
  (wetnessUniform as unknown as { value: number }).value = THREE.MathUtils.clamp(absorbed, 0, 1);
  (wetFilmUniform as unknown as { value: number }).value = THREE.MathUtils.clamp(film, 0, 1);
  (wetPondUniform as unknown as { value: number }).value = THREE.MathUtils.clamp(pond, 0, 1);
}
/** The three ground levels as the ground reads them (diagnostics). */
export function getWetLevels(): { absorbed: number; film: number; pond: number } {
  return { absorbed: (wetnessUniform as unknown as { value: number }).value, film: (wetFilmUniform as unknown as { value: number }).value, pond: (wetPondUniform as unknown as { value: number }).value };
}
/** Blowing dust over the ground (0..1) and the wind it streams along — the sandstorm's sand sheets. */
export function setDust(v: number, windX: number, windZ: number): void {
  (dustUniform as unknown as { value: number }).value = THREE.MathUtils.clamp(v, 0, 1);
  const w = (dustWindUniform as unknown as { value: THREE.Vector2 }).value;
  const l = Math.hypot(windX, windZ) || 1; w.set(windX / l, windZ / l);
}
export function getWetness(): number { return (wetnessUniform as unknown as { value: number }).value; }
